import { GoogleSignIn } from "./components/GoogleSignIn";
import { catalogSortOptions, sortCatalog, CatalogSort } from "./lib/catalog-sort";
import { DeliveryMap } from "./components/DeliveryMap";
import { CafeGallery, CafeGallerySettings, CafeGallerySettingsHeading } from "./components/CafeGallery";
import { SettingsSectionHeading } from "./components/SettingsSectionHeading";
import { ArtworkLibrarySettings } from "./components/ArtworkLibrarySettings";
import { deliveryLocationError } from "./lib/delivery.mjs";
import { Children, ReactNode, FormEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Armchair,
  ArrowUpRight,
  BadgeDollarSign,
  CalendarDays,
  Camera,
  ChefHat,
  ChevronDown,
  ClipboardList,
  CircleCheck,
  Coins,
  CreditCard,
  Download,
  FileText,
  Flame,
  Gift,
  Hash,
  ImagePlus,
  KeyRound,
  Leaf,
  LogIn,
  Mail,
  MapPin,
  PackageCheck,
  Palette,
  PanelLeftClose,
  PanelLeftOpen,
  Phone,
  Plus,
  Printer,
  QrCode,
  RotateCcw,
  Save,
  Search,
  Settings,
  ShoppingCart,
  SlidersHorizontal,
  Star,
  Store,
  Trash2,
  Truck,
  Utensils,
  User,
  UserPlus,
  X
} from "lucide-react";
import { adminRequest, apiRequest, money } from "./lib/api";
import { normalizeSidebarProp, readArtworkLibrary, readSidebarArtwork, SidebarPropId } from "./lib/sidebar-props";
import { settingsSectionLabels, settingsSectionPayload, SettingsSectionId } from "./lib/settings-sections";
import type {
  AdminDashboardPayload,
  BootstrapPayload,
  CartItem,
  Category,
  Combo,
  CustomerAccount,
  CustomerRewards,
  DeliveryArea,
  Feedback,
  ItemFeedbackInput,
  MenuItem,
  Nutrition,
  Order,
  OrderItemStatus,
  OrderStatus,
  TableOccupancy
} from "./types";

type View = "menu" | "combos" | "cart" | "orders" | "rewards" | "gallery" | "admin";
type AdminTab = "orders" | "online-delivery" | "delivery-status" | "tables" | "catalog" | "feedback" | "settings";
type CustomerSession = { customer: CustomerAccount; credential: string };
type AuthMode = "login" | "signup" | "reset" | "set-password";
type CheckoutForm = {
  name: string;
  mobile: string;
  email: string;
  notes: string;
  tableNumber: string;
  fulfillmentType: "cafe" | "delivery";
  deliveryAreaId: string;
  deliveryAddress: string;
  deliveryLatitude: number | null;
  deliveryLongitude: number | null;
  deliveryPinnedAddress: string;
};

type ProfileForm = Pick<CheckoutForm, "name" | "mobile" | "deliveryAreaId" | "deliveryAddress" | "deliveryLatitude" | "deliveryLongitude" | "deliveryPinnedAddress">;
function profileFormFromCustomer(customer: CustomerAccount): ProfileForm {
  return { name: customer.name, mobile: customer.mobile, deliveryAreaId: customer.deliveryAreaId, deliveryAddress: customer.deliveryAddress, deliveryLatitude: customer.deliveryLatitude ?? null, deliveryLongitude: customer.deliveryLongitude ?? null, deliveryPinnedAddress: customer.deliveryPinnedAddress || customer.deliveryAddress };
}

const adminTabs: AdminTab[] = [
  "online-delivery",
  "delivery-status",
  "orders",
  "tables",
  "catalog",
  "feedback",
  "settings"
];

const adminTabLabels: Record<AdminTab, string> = {
  "online-delivery": "Online Orders",
  "delivery-status": "Delivery Status",
  orders: "Cafe Orders",
  tables: "Table Status",
  catalog: "Catalog",
  feedback: "Feedback",
  settings: "Settings"
};

const uiThemes = [
  {
    id: "garden-gold",
    label: "Garden & Gold",
    description: "Bright golden yellow, fresh light green, and white with a soft, restful background.",
    font: "Avenir Next / Segoe UI modern sans",
    colors: ["#ffd338", "#b9e582", "#ffffff"]
  },
  {
    id: "default",
    label: "Default — Digital Cafe",
    description: "The existing clean cafe interface with crisp corners and tomato accents.",
    font: "Inter / Aptos modern sans",
    colors: ["#e84d3d", "#149b68", "#232933"]
  },
  {
    id: "warm-mocha",
    label: "Warm Mocha",
    description: "An inviting editorial template with warm coffee tones and contemporary typography.",
    font: "Aptos / Segoe UI modern sans",
    colors: ["#b75d2b", "#4f7a49", "#3b2923"]
  },
  {
    id: "fresh-mint",
    label: "Fresh Mint",
    description: "A soft, rounded template with fresh green accents and friendly typography.",
    font: "Avenir Next / Segoe UI modern sans",
    colors: ["#16845f", "#2f7a7b", "#163b32"]
  },
  {
    id: "ocean-breeze",
    label: "Ocean Breeze",
    description: "A compact modern template with cool blue tones and streamlined corners.",
    font: "Inter / Segoe UI modern sans",
    colors: ["#2463a9", "#0c8c95", "#102a43"]
  },
  {
    id: "nordic-minimal",
    label: "Nordic Minimal",
    description: "A restrained monochrome template with precise spacing and understated accents.",
    font: "Helvetica Neue / Segoe UI Variable",
    colors: ["#18181b", "#64748b", "#f5f5f4"]
  },
  {
    id: "neo-lavender",
    label: "Neo Lavender",
    description: "A glassy, rounded template with violet gradients and bright cyan highlights.",
    font: "Manrope / Segoe UI Variable",
    colors: ["#7c3aed", "#06b6d4", "#24134d"]
  },
  {
    id: "sunset-studio",
    label: "Sunset Studio",
    description: "A confident modern template with coral, amber, and indigo color blocking.",
    font: "Avenir Next / Segoe UI Variable",
    colors: ["#f43f5e", "#f59e0b", "#312e81"]
  },
  {
    id: "midnight-neon",
    label: "Midnight Neon",
    description: "A polished dark template with luminous violet and cyan accents.",
    font: "Inter / Segoe UI Variable",
    colors: ["#8b5cf6", "#22d3ee", "#080b14"]
  }
] as const;

type UiThemeId = (typeof uiThemes)[number]["id"];

function normalizeUiTheme(value: string | undefined): UiThemeId {
  return uiThemes.some((theme) => theme.id === value) ? (value as UiThemeId) : "default";
}

function cafeDate(value: string | Date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Rangoon", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(value));
  return ["year", "month", "day"].map(type => parts.find(part => part.type === type)?.value).join("-");
}

const orderStatuses: OrderStatus[] = ["received", "processing", "served", "paid", "complete", "returned"];

const orderStatusIcons = { received: ClipboardList, processing: Flame, served: Utensils, paid: CreditCard, complete: PackageCheck, returned: RotateCcw };
const adminTabIcons = { orders: ClipboardList, "online-delivery": Truck, "delivery-status": MapPin, tables: Armchair, catalog: Utensils, feedback: Star, settings: Settings };

const statusLabels: Record<OrderStatus, string> = {
  received: "Ordered",
  processing: "Processing",
  served: "Served",
  paid: "Paid",
  complete: "Complete",
  returned: "Returned"
};

function displayOrderStatus(status: OrderStatus, fulfillmentType?: Order["fulfillmentType"]) {
  return fulfillmentType === "delivery" && status === "served" ? "Delivery In Progress" : statusLabels[status];
}

const orderItemStatusLabels: Record<OrderItemStatus, string> = {
  active: "Active",
  cancelled: "Cancelled",
  returned: "Returned"
};

const fallbackImages = {
  menu:
    "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=900&q=80",
  combo:
    "https://images.unsplash.com/photo-1543352634-a1c51d9f1fa7?auto=format&fit=crop&w=900&q=80"
};

function cartItemFromCatalog(item: MenuItem | Combo, itemType: "menu" | "combo", quantity: number): CartItem {
  const isCombo = "totalNutrition" in item;

  return {
    key: `${itemType}:${item.id}`,
    itemType,
    itemId: item.id,
    name: item.name,
    imageUrl: item.imageUrl || (isCombo ? fallbackImages.combo : fallbackImages.menu),
    unitPriceCents: item.priceCents,
    quantity,
    nutrition: isCombo ? item.totalNutrition : item.nutrition,
    details: isCombo ? item.items.map(({ item: comboItem, quantity: itemQuantity }) => `${itemQuantity}x ${comboItem.name}`) : item.ingredients
  };
}

const emptyCategoryForm = {
  id: "",
  name: "",
  description: "",
  sortOrder: 99,
  isActive: true
};

const emptyMenuForm = {
  id: "",
  categoryId: "",
  name: "",
  description: "",
  price: "",
  imageUrl: "",
  ingredientsText: "",
  calories: "0",
  carbs: "0",
  protein: "0",
  fat: "0",
  fiber: "0",
  rating: "4.7",
  ratingCount: "0",
  isBestSeller: false,
  isAvailable: true,
  sortOrder: 99
};

const emptyComboForm = {
  id: "",
  name: "",
  description: "",
  price: "",
  imageUrl: "",
  rating: "4.7",
  ratingCount: "0",
  isBestSeller: false,
  isAvailable: true,
  sortOrder: 99
};

const emptyDeliveryAreaForm = {
  centerLatitude: null as number | null,
  centerLongitude: null as number | null,
  centerAddress: "",
  radiusMiles: 1,
  id: "",
  name: "",
  charge: "",
  sortOrder: 99,
  isActive: true
};

function menuFormFromItem(item: MenuItem) {
  return {
    id: item.id,
    categoryId: item.categoryId,
    name: item.name,
    description: item.description,
    price: String(item.price),
    imageUrl: item.imageUrl,
    ingredientsText: item.ingredients.join(", "),
    calories: String(item.nutrition.calories ?? 0),
    carbs: String(item.nutrition.carbs ?? 0),
    protein: String(item.nutrition.protein ?? 0),
    fat: String(item.nutrition.fat ?? 0),
    fiber: String(item.nutrition.fiber ?? 0),
    rating: String(item.rating),
    ratingCount: String(item.ratingCount),
    isBestSeller: item.isBestSeller,
    isAvailable: item.isAvailable,
    sortOrder: item.sortOrder
  };
}

function App() {
  const adminRoute = isAdminRoute();
  const scannedTableNumber = scannedTableNumberFromLocation();
  const tableQrPresent = new URLSearchParams(window.location.search).has("table");
  const [tableEntry, setTableEntry] = useState<"checking" | "available" | "occupied" | "error">(tableQrPresent && !adminRoute ? "checking" : "available");
  const [tableEntryMessage, setTableEntryMessage] = useState("");
  const [tableSessionToken, setTableSessionToken] = useState(() => {
    try { return scannedTableNumber ? window.localStorage.getItem(`digital-cafe-table-session:${scannedTableNumber}`) ?? "" : ""; } catch { return ""; }
  });
  const [activeView, setActiveView] = useState<View>(() => (adminRoute ? "admin" : "menu"));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return window.localStorage.getItem("digital-cafe-sidebar-collapsed") === "true"; } catch { return false; }
  });
  const [activeAdminTab, setActiveAdminTab] = useState<AdminTab>("online-delivery");
  const [bootstrap, setBootstrap] = useState<BootstrapPayload | null>(null);
  const [publicLoading, setPublicLoading] = useState(true);
  const [publicError, setPublicError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [maxPriceCents, setMaxPriceCents] = useState(2500);
  const [menuSort, setMenuSort] = useState<CatalogSort>("price-asc");
  const [comboSort, setComboSort] = useState<CatalogSort>("price-asc");
  const [comboSearch, setComboSearch] = useState("");
  const [comboPriceLimit, setComboPriceLimit] = useState<number | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartRefreshing, setCartRefreshing] = useState(false);
  const [ratingsOpen, setRatingsOpen] = useState(false);
  const [selectedRatingItem, setSelectedRatingItem] = useState<{ itemType: "menu" | "combo"; itemId: string; name: string } | null>(null);
  useEffect(() => { setRatingsOpen(false); }, [activeView]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [customer, setCustomer] = useState<CheckoutForm>(() => ({
    name: "",
    mobile: "",
    email: "",
    notes: "",
    tableNumber: scannedTableNumber,
    fulfillmentType: scannedTableNumber ? "cafe" : "delivery",
    deliveryAreaId: "",
    deliveryAddress: "",
    deliveryLatitude: null,
    deliveryLongitude: null,
    deliveryPinnedAddress: ""
  }));
  const [isGuestCheckout, setIsGuestCheckout] = useState(false);
  const [customerSession, setCustomerSession] = useState<CustomerSession | null>(null);
  const [guestOrders, setGuestOrders] = useState<Order[]>([]);
  const [orderSuccess, setOrderSuccess] = useState<Order | null>(null);
  const [customerOrders, setCustomerOrders] = useState<Order[]>([]);
  const [customerOrdersRefreshing, setCustomerOrdersRefreshing] = useState(false);
  const [customerRewards, setCustomerRewards] = useState<CustomerRewards | null>(null);
  const [customerMessage, setCustomerMessage] = useState("");
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [authForm, setAuthForm] = useState({ email: "", password: "", newPassword: "", confirmPassword: "" });
  const [authVerification, setAuthVerification] = useState<{ token: string; purpose: "signup" | "reset"; email: string } | null>(null);
  const [authMessage, setAuthMessage] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const openingAuthShown = useRef(false);
  const [deliveryAuthPending, setDeliveryAuthPending] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileForm, setProfileForm] = useState<ProfileForm>({ name: "", mobile: "", deliveryAreaId: "", deliveryAddress: "", deliveryLatitude: null, deliveryLongitude: null, deliveryPinnedAddress: "" });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");
  const [useRewardPoints, setUseRewardPoints] = useState(false);
  const [orderMessage, setOrderMessage] = useState("");
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [feedbackOrder, setFeedbackOrder] = useState<Order | null>(null);
  const [adminPin, setAdminPin] = useState(() => window.localStorage.getItem("digital-cafe-admin-pin") ?? "");
  const [themePreview, setThemePreview] = useState<UiThemeId | null>(null);
  const [adminData, setAdminData] = useState<AdminDashboardPayload | null>(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminMessage, setAdminMessage] = useState("");
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [menuForm, setMenuForm] = useState(emptyMenuForm);
  const [menuDialogOpen, setMenuDialogOpen] = useState(false);
  const [comboForm, setComboForm] = useState(emptyComboForm);
  const [deliveryAreaForm, setDeliveryAreaForm] = useState(emptyDeliveryAreaForm);
  const [comboSelections, setComboSelections] = useState<Record<string, number>>({});
  const [adminOrderStatusFilter, setAdminOrderStatusFilter] = useState<OrderStatus | "all">("all");
  const [focusedOrderId, setFocusedOrderId] = useState<string | null>(null);
  const [historyRange, setHistoryRange] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    return { from: today, to: today };
  });
  const [deliveryStatusRange, setDeliveryStatusRange] = useState(() => ({ from: cafeDate(), to: cafeDate() }));

  const tableIdentityHeaders = useMemo<Record<string, string>>(() => ({
    ...(tableSessionToken ? { "X-Table-Session": tableSessionToken } : {}),
    ...(customerSession ? { "X-Customer-Id": customerSession.customer.id, "X-Customer-Password": customerSession.credential } : {})
  }), [tableSessionToken, customerSession?.customer.id, customerSession?.credential]);
  const checkTableEntry = useCallback(async () => {
    if (adminRoute || !tableQrPresent) return;
    if (!scannedTableNumber) { setTableEntry("error"); setTableEntryMessage("This table QR code is invalid. Please scan another table."); return; }
    setTableEntry("checking");
    try {
      const table = await apiRequest<{ tableNumber: string; isOccupied: boolean; canOrder: boolean }>(`/api/tables/${scannedTableNumber}/availability`, { cache: "no-store", headers: tableIdentityHeaders });
      setTableEntry(table.canOrder ? "available" : "occupied");
      setTableEntryMessage(!table.canOrder ? "The table is currently occupied. Please try another table." : "");
    } catch (error) { setTableEntry("error"); setTableEntryMessage(error instanceof Error ? error.message : "Unable to check this table. Please try again."); }
  }, [adminRoute, tableQrPresent, scannedTableNumber, tableIdentityHeaders]);
  useEffect(() => { void checkTableEntry(); }, [checkTableEntry]);

  const refreshPublic = useCallback(async () => {
    setPublicLoading(true);
    setPublicError("");

    try {
      const data = await apiRequest<BootstrapPayload>("/api/bootstrap", { cache: "no-store" });
      setBootstrap(data);
    } catch (error) {
      setPublicError(error instanceof Error ? error.message : "Unable to load menu.");
    } finally {
      setPublicLoading(false);
    }
  }, []);

  const refreshAdmin = useCallback(async () => {
    if (!adminPin) return;
    setAdminLoading(true);
    setAdminMessage("");

    try {
      const data = await adminRequest<AdminDashboardPayload>(adminPin, "/api/admin/dashboard");
      setAdminData(data);
      window.localStorage.setItem("digital-cafe-admin-pin", adminPin);
    } catch (error) {
      setAdminMessage(error instanceof Error ? error.message : "Unable to load admin dashboard.");
    } finally {
      setAdminLoading(false);
    }
  }, [adminPin]);

  useEffect(() => {
    void refreshPublic();
  }, [refreshPublic]);

  useEffect(() => {
    if (activeView === "admin" && adminPin) {
      void refreshAdmin();
    }
  }, [activeView, adminPin, refreshAdmin]);

  useEffect(() => {
    if (
      activeView !== "admin" ||
      !["orders", "online-delivery"].includes(activeAdminTab) ||
      !focusedOrderId
    ) return;
    window.setTimeout(() => {
      document.getElementById(`order-${focusedOrderId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  }, [activeView, activeAdminTab, focusedOrderId, adminData?.orders.length]);

  useEffect(() => {
    if (!customerSession) return;
    setCustomer((current) => ({
      ...current,
      name: customerSession.customer.name,
      mobile: customerSession.customer.mobile,
      email: customerSession.customer.email,
      deliveryAreaId: current.deliveryLatitude != null ? current.deliveryAreaId : customerSession.customer.deliveryAreaId,
      deliveryAddress: current.deliveryLatitude != null ? current.deliveryAddress : customerSession.customer.deliveryAddress,
      deliveryLatitude: current.deliveryLatitude ?? customerSession.customer.deliveryLatitude ?? null,
      deliveryLongitude: current.deliveryLongitude ?? customerSession.customer.deliveryLongitude ?? null,
      deliveryPinnedAddress: current.deliveryLatitude != null ? current.deliveryPinnedAddress : customerSession.customer.deliveryPinnedAddress || customerSession.customer.deliveryAddress
    }));
    setProfileForm(profileFormFromCustomer(customerSession.customer));
    setIsGuestCheckout(false);
  }, [customerSession]);

  useEffect(() => {
    if (!customerSession || !deliveryAuthPending) return;
    setCustomer((current) => ({ ...current, fulfillmentType: scannedTableNumber ? "cafe" : "delivery" }));
    setIsGuestCheckout(false);
    setDeliveryAuthPending(false);
  }, [customerSession, deliveryAuthPending]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("authToken")?.trim();
    const rawPurpose = params.get("authPurpose");
    if (!token || (rawPurpose !== "signup" && rawPurpose !== "reset")) return;

    setAuthOpen(true);
    setAuthMode("set-password");
    setAuthBusy(true);
    setAuthMessage("Verifying your secure email link...");
    void apiRequest<{ email: string; purpose: "signup" | "reset" }>("/api/customers/auth/verify", {
      method: "POST",
      body: JSON.stringify({ token, purpose: rawPurpose })
    })
      .then((payload) => {
        setAuthVerification({ token, purpose: payload.purpose, email: payload.email });
        setAuthForm((current) => ({ ...current, email: payload.email, newPassword: "", confirmPassword: "" }));
        setAuthMessage(`Email verified. ${payload.purpose === "signup" ? "Create" : "Choose"} your new password.`);
      })
      .catch((error) => setAuthMessage(error instanceof Error ? error.message : "This email link could not be verified."))
      .finally(() => setAuthBusy(false));
  }, []);

  useEffect(() => {
    if (customerSession) return;
    setCustomerRewards(null);
    setUseRewardPoints(false);
  }, [customerSession]);



  const categories = bootstrap?.categories ?? [];
  const menuItems = bootstrap?.menuItems ?? [];
  const combos = bootstrap?.combos ?? [];
  const deliveryAreas = bootstrap?.deliveryAreas ?? [];
  const customerFeedback = bootstrap?.feedback ?? [];
  const showItemRatings = !adminRoute && ["menu", "combos", "cart"].includes(activeView);
  const activeRatingItem = selectedRatingItem && ((activeView === "menu" && selectedRatingItem.itemType === "menu") || (activeView === "combos" && selectedRatingItem.itemType === "combo")) ? selectedRatingItem : null;
  const ratingKeys = new Set(activeView === "cart" ? cart.map(item => `${item.itemType}:${item.itemId}`) : activeRatingItem ? [`${activeRatingItem.itemType}:${activeRatingItem.itemId}`] : []);
  const scopedFeedback: Feedback[] = customerFeedback.flatMap(entry => (entry.items ?? []).filter(item => ratingKeys.has(`${item.itemType}:${item.itemId}`)).map(item => ({ ...entry, id: `${entry.id}:${item.orderItemId}`, rating: item.rating, message: item.message, itemName: item.name })));
  const ratingsContext = activeView === "cart" ? "Reviews for items in My Cart" : activeRatingItem ? `Reviews for ${activeRatingItem.name}` : "Select an item's stars to view its ratings";
  function selectItemRatings(itemType: "menu" | "combo", itemId: string, name: string) {
    setSelectedRatingItem({ itemType, itemId, name });
    if (window.matchMedia?.("(max-width: 1280px)").matches) setRatingsOpen(true);
  }
  const settings = bootstrap?.settings ?? {};
  useEffect(() => {
    if (adminRoute || !bootstrap || customerSession || tableEntry !== "available" || openingAuthShown.current) return;
    openingAuthShown.current = true;
    const params = new URLSearchParams(window.location.search);
    if (params.get("authToken") && ["signup", "reset"].includes(params.get("authPurpose") || "")) return;
    setAuthMode("login");
    setAuthOpen(true);
  }, [adminRoute, bootstrap, customerSession, tableEntry]);
  const currency = settings.currency ?? "USD";
  const tableOptions = useMemo(() => buildTableOptions(settings.table_count), [settings.table_count]);
  const customerInitial = customerSession?.customer.name.trim().charAt(0).toUpperCase() || "C";
  const uiTheme = themePreview ?? normalizeUiTheme(settings.ui_theme);
  const topbarBackgroundImage = settings.topbar_image_url?.trim() ?? "";

  useEffect(() => {
    document.documentElement.dataset.theme = uiTheme;
  }, [uiTheme]);

  useEffect(() => {
    if (activeAdminTab !== "settings") setThemePreview(null);
  }, [activeAdminTab]);

  const highestPriceCents = useMemo(
    () => Math.max(1500, ...menuItems.map((item) => item.priceCents), ...combos.map((combo) => combo.priceCents)),
    [menuItems, combos]
  );

  useEffect(() => {
    if (highestPriceCents > maxPriceCents && maxPriceCents === 2500) {
      setMaxPriceCents(highestPriceCents);
    }
  }, [highestPriceCents, maxPriceCents]);

  const menuItemsMatchingFilters = useMemo(() => {
    const query = search.trim().toLowerCase();

    return menuItems.filter((item) => {
      const matchesSearch =
        !query ||
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.ingredients.some((ingredient) => ingredient.toLowerCase().includes(query));
      const matchesPrice = item.priceCents <= maxPriceCents;
      return matchesSearch && matchesPrice;
    });
  }, [menuItems, search, maxPriceCents]);

  const filteredMenu = useMemo(
    () =>
      sortCatalog(categoryId === "all"
        ? menuItemsMatchingFilters
        : menuItemsMatchingFilters.filter((item) => item.categoryId === categoryId), menuSort),
    [categoryId, menuItemsMatchingFilters, menuSort]
  );
  const highestComboPrice = Math.max(1500, ...combos.map(combo => combo.priceCents));
  const comboMaxPrice = comboPriceLimit ?? highestComboPrice;
  const comboFiltersAreActive = Boolean(comboSearch.trim()) || comboMaxPrice < highestComboPrice || comboSort !== "price-asc";
  const visibleCombos = useMemo(() => {
    const query = comboSearch.trim().toLowerCase();
    return sortCatalog(combos.filter(combo => combo.priceCents <= comboMaxPrice && `${combo.name} ${combo.description} ${combo.items.map(({ item }) => item.name).join(" ")}`.toLowerCase().includes(query)), comboSort);
  }, [combos, comboSearch, comboMaxPrice, comboSort]);

  const categoryCounts = useMemo(
    () =>
      Object.fromEntries([
        ["all", menuItemsMatchingFilters.length],
        ...categories.map((category) => [
          category.id,
          menuItemsMatchingFilters.filter((item) => item.categoryId === category.id).length
        ])
      ]),
    [categories, menuItemsMatchingFilters]
  );
  const filtersAreActive = Boolean(search.trim()) || categoryId !== "all" || maxPriceCents < highestPriceCents || menuSort !== "price-asc";

  const cafeOrders = useMemo(() => {
    const orders = (adminData?.orders ?? []).filter((order) => order.fulfillmentType === "cafe");
    return adminOrderStatusFilter === "all" ? orders : orders.filter((order) => order.status === adminOrderStatusFilter);
  }, [adminData?.orders, adminOrderStatusFilter]);
  const onlineDeliveryOrders = useMemo(() => {
    const orders = (adminData?.orders ?? []).filter((order) => order.fulfillmentType === "delivery");
    return adminOrderStatusFilter === "all" ? orders : orders.filter((order) => order.status === adminOrderStatusFilter);
  }, [adminData?.orders, adminOrderStatusFilter]);

  const cartTotals = useMemo(() => {
    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
    const totalCents = cart.reduce((sum, item) => sum + item.unitPriceCents * item.quantity, 0);
    const nutrition = cart.reduce<Nutrition>((sum, item) => {
      Object.entries(item.nutrition).forEach(([key, value]) => {
        sum[key] = Math.round(((sum[key] ?? 0) + value * item.quantity) * 10) / 10;
      });
      return sum;
    }, {});

    return { totalItems, totalCents, nutrition };
  }, [cart]);

  const rewardBalance = customerRewards?.balance ?? 0;
  const rewardPointsToUse = useRewardPoints && customerSession ? Math.min(rewardBalance, Math.floor(cartTotals.totalCents / 100)) : 0;
  const rewardDiscountCents = rewardPointsToUse * 100;
  const deliveryChargeCents =
    customer.fulfillmentType === "delivery"
      ? deliveryAreas.find((area) => area.id === customer.deliveryAreaId)?.chargeCents ?? 0
      : 0;
  const checkoutTotalCents = Math.max(0, cartTotals.totalCents - rewardDiscountCents + deliveryChargeCents);

  useEffect(() => {
    if (!useRewardPoints || rewardPointsToUse > 0) return;
    setUseRewardPoints(false);
  }, [rewardPointsToUse, useRewardPoints]);

  function addToCart(item: MenuItem | Combo, itemType: "menu" | "combo") {
    const key = `${itemType}:${item.id}`;

    setCart((current) => {
      const existing = current.find((entry) => entry.key === key);
      if (existing) {
        return current.map((entry) => (entry.key === key ? cartItemFromCatalog(item, itemType, entry.quantity + 1) : entry));
      }

      return [...current, cartItemFromCatalog(item, itemType, 1)];
    });
  }

  async function refreshCartItems() {
    setCartRefreshing(true);
    setOrderMessage("");
    setPublicError("");

    try {
      const data = await apiRequest<BootstrapPayload>("/api/bootstrap", { cache: "no-store" });
      setBootstrap(data);
      setCart((current) =>
        current.map((entry) => {
          if (entry.itemType === "menu") {
            const item = data.menuItems.find((candidate) => candidate.id === entry.itemId);
            return item ? cartItemFromCatalog(item, "menu", entry.quantity) : entry;
          }

          const combo = data.combos.find((candidate) => candidate.id === entry.itemId);
          return combo ? cartItemFromCatalog(combo, "combo", entry.quantity) : entry;
        })
      );
    } catch (error) {
      setPublicError(error instanceof Error ? error.message : "Unable to refresh cart items.");
    } finally {
      setCartRefreshing(false);
    }
  }

  function changeCartQuantity(key: string, delta: number) {
    setCart((current) =>
      current
        .map((item) => (item.key === key ? { ...item, quantity: Math.max(0, item.quantity + delta) } : item))
        .filter((item) => item.quantity > 0)
    );
  }

  async function placeOrder(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!scannedTableNumber && !customerSession) {
      setDeliveryAuthPending(true);
      openAuth("login");
      return;
    }

    if (customer.fulfillmentType === "delivery") {
      const area = deliveryAreas.find(area => area.id === customer.deliveryAreaId);
      const error = area ? deliveryLocationError(area, customer.deliveryLatitude, customer.deliveryLongitude) : "Select a delivery area.";
      if (error) { setOrderMessage(error); return; }
    }
    setSubmittingOrder(true);
    setOrderMessage("");

    try {
      if (scannedTableNumber) {
        const table = await apiRequest<{ isOccupied: boolean; canOrder: boolean }>(`/api/tables/${scannedTableNumber}/availability`, { cache: "no-store", headers: tableIdentityHeaders });
        if (!table.canOrder) { setTableEntry("occupied"); setTableEntryMessage("The table is currently occupied. Please try another table."); return; }
      }
      const payload = await apiRequest<{ order: Order; tableSessionToken?: string }>("/api/orders", {
        method: "POST",
        body: JSON.stringify({
          isGuest: customer.fulfillmentType === "cafe" && isGuestCheckout && !customerSession,
          customer,
          customerId: customerSession?.customer.id,
          customerPassword: customerSession?.credential,
          useRewardPoints: useRewardPoints && rewardPointsToUse > 0,
          fulfillmentType: scannedTableNumber ? "cafe" : "delivery",
          deliveryAreaId: customer.deliveryAreaId,
          deliveryAddress: customer.deliveryAddress,
          deliveryLatitude: customer.deliveryLatitude,
          deliveryLongitude: customer.deliveryLongitude,
          tableNumber: scannedTableNumber,
          tableSessionToken,
          notes: customer.notes,
          items: cart.map((item) => ({
            itemType: item.itemType,
            itemId: item.itemId,
            quantity: item.quantity
          }))
        })
      });

      if (scannedTableNumber && payload.tableSessionToken) {
        setTableSessionToken(payload.tableSessionToken);
        try { window.localStorage.setItem(`digital-cafe-table-session:${scannedTableNumber}`, payload.tableSessionToken); } catch { /* The current page can still reuse the in-memory session. */ }
      }
      setCart([]);
      setUseRewardPoints(false);
      setOrderMessage("");
      if (customerSession) setCustomerOrders(current => [payload.order, ...current.filter(order => order.id !== payload.order.id)]);
      else setGuestOrders(current => [payload.order, ...current.filter(order => order.id !== payload.order.id)]);
      setActiveView("orders");
      setOrderSuccess(payload.order);
      if (customerSession) {
        await refreshCustomerOrders();
        await refreshCustomerRewards();
      }
      if (adminData) void refreshAdmin();
    } catch (error) {
      setOrderMessage(error instanceof Error ? error.message : "Order could not be placed.");
      if (scannedTableNumber && error instanceof Error && error.message.includes("currently occupied")) {
        setTableEntry("occupied");
        setTableEntryMessage("The table is currently occupied. Please try another table.");
      }
    } finally {
      setSubmittingOrder(false);
    }
  }

  async function submitOrderFeedback(order: Order, items: ItemFeedbackInput[], message: string) {
    if (!customerSession || order.status !== "complete") throw new Error("Sign in to review a completed order.");
    await apiRequest("/api/feedback", { method: "POST", body: JSON.stringify({ orderId: order.id, customerId: customerSession.customer.id, customerPassword: customerSession.credential, items, message }) });
    await Promise.all([refreshPublic(), refreshCustomerOrders()]);
    if (adminData) void refreshAdmin();
    setFeedbackOrder(null);
    setCustomerMessage("Thank you. Your feedback and item ratings were received.");
  }

  function openAuth(mode: AuthMode = "login") {
    setAuthMode(mode);
    setAuthMessage("");
    setAuthOpen(true);
    setAuthForm((current) => ({
      ...current,
      email: current.email || customerSession?.customer.email || "",
      password: "",
      newPassword: "",
      confirmPassword: ""
    }));
  }

  function selectOnlineDelivery() {
    if (scannedTableNumber) return;
    if (!customerSession) {
      setDeliveryAuthPending(true);
      openAuth("login");
      return;
    }

    setIsGuestCheckout(false);
    setCustomer((current) => ({ ...current, fulfillmentType: scannedTableNumber ? "cafe" : "delivery" }));
  }

  async function signInGoogle(credential: string, nonce: string) {
    if (authBusy) return;
    setAuthBusy(true); setAuthMessage("");
    try {
      const payload = await apiRequest<{ customer: CustomerAccount; orders: Order[]; rewards: CustomerRewards; credential: string }>("/api/customers/auth/google", { method: "POST", body: JSON.stringify({ credential, nonce }) });
      setCustomerSession({ customer: payload.customer, credential: payload.credential });
      setCustomerOrders(payload.orders); setCustomerRewards(payload.rewards); setAuthOpen(false);
      if (!payload.customer.mobile) { setProfileOpen(true); setProfileMessage("Add your mobile number to finish your profile before ordering."); }
    } catch (error) { throw new Error(error instanceof Error ? error.message : "Google sign-in failed."); }
    finally { setAuthBusy(false); }
  }

  async function submitAuth(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAuthBusy(true);
    setAuthMessage("");

    try {
      if (authMode === "login") {
        const payload = await apiRequest<{ customer: CustomerAccount; orders: Order[]; rewards?: CustomerRewards }>("/api/customers/login", {
          method: "POST",
          body: JSON.stringify({ email: authForm.email, password: authForm.password })
        });
        setCustomerSession({ customer: payload.customer, credential: authForm.password });
        setCustomerOrders(payload.orders);
        setCustomerRewards(payload.rewards ?? null);
        setCustomerMessage("");
        setAuthOpen(false);
        return;
      }

      if (authMode === "signup" || authMode === "reset") {
        const payload = await apiRequest<{ message: string }>("/api/customers/auth/request", {
          method: "POST",
          body: JSON.stringify({
            email: authForm.email,
            purpose: authMode,
            redirectUrl: `${window.location.origin}${window.location.pathname}`
          })
        });
        setAuthMessage(payload.message);
        return;
      }

      if (!authVerification) throw new Error("Verify a fresh email link before setting your password.");
      if (authForm.newPassword !== authForm.confirmPassword) throw new Error("The two password entries do not match.");
      const payload = await apiRequest<{
        customer: CustomerAccount;
        orders: Order[];
        rewards?: CustomerRewards;
        message?: string;
      }>("/api/customers/auth/complete", {
        method: "POST",
        body: JSON.stringify({
          token: authVerification.token,
          purpose: authVerification.purpose,
          newPassword: authForm.newPassword
        })
      });
      setCustomerSession({ customer: payload.customer, credential: authForm.newPassword });
      setCustomerOrders(payload.orders);
      setCustomerRewards(payload.rewards ?? null);
      setCustomerMessage("");
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("authToken");
      cleanUrl.searchParams.delete("authPurpose");
      window.history.replaceState({}, "", `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
      setAuthVerification(null);
      setAuthOpen(false);
    } catch (error) {
      setAuthMessage(error instanceof Error ? error.message : "Customer authentication failed.");
    } finally {
      setAuthBusy(false);
    }
  }

  function openCustomerProfile() {
    if (!customerSession) return;
    setProfileForm(profileFormFromCustomer(customerSession.customer));
    setProfileMessage(""); setProfileOpen(true);
  }

  async function saveCustomerProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!customerSession || profileSaving) return;
    if (profileForm.deliveryAreaId || profileForm.deliveryLatitude != null || profileForm.deliveryLongitude != null) {
      const area = deliveryAreas.find(area => area.id === profileForm.deliveryAreaId);
      const error = area ? deliveryLocationError(area, profileForm.deliveryLatitude, profileForm.deliveryLongitude) : "Select an available delivery area for your address pin.";
      if (error) { setProfileMessage(error); return; }
    }
    setProfileSaving(true);
    setProfileMessage("");
    try {
      const payload = await apiRequest<{ customer: CustomerAccount; message?: string }>("/api/customers/profile", {
        method: "PUT",
        body: JSON.stringify({
          email: customerSession.customer.email,
          password: customerSession.credential,
          ...profileForm
        })
      });
      setCustomerSession((current) => (current ? { ...current, customer: payload.customer } : null));
      setCustomer(current => ({ ...current, ...profileFormFromCustomer(payload.customer) }));
      setProfileMessage(payload.message ?? "Profile saved.");
    } catch (error) {
      setProfileMessage(error instanceof Error ? error.message : "Profile could not be saved.");
    } finally { setProfileSaving(false); }
  }

  function signOutCustomer() {
    if (customerSession?.credential.startsWith("session:")) void apiRequest("/api/customers/logout", { method: "POST", body: JSON.stringify({ credential: customerSession.credential }) }).catch(() => {});
    setGuestOrders([]);
    setOrderSuccess(null);
    setTableSessionToken("");
    try { for (let table = 1; table <= 100; table++) window.localStorage.removeItem(`digital-cafe-table-session:${table}`); } catch { /* Storage may be unavailable. */ }
    setCustomerSession(null);
    setCustomerOrders([]);
    setFeedbackOrder(null);
    setCustomerRewards(null);
    setCustomer((current) => ({ ...current, name: "", mobile: "", email: "", deliveryAreaId: "", deliveryAddress: "", deliveryLatitude: null, deliveryLongitude: null, deliveryPinnedAddress: "", fulfillmentType: scannedTableNumber ? "cafe" : "delivery" }));
    setDeliveryAuthPending(false);
    setProfileOpen(false);
    setCustomerMessage("Signed out.");
  }

  async function refreshCustomerOrders() {
    if (!customerSession) {
      if (!tableSessionToken || !scannedTableNumber) return;
      setCustomerOrdersRefreshing(true);
      try { const payload = await apiRequest<{ orders: Order[] }>("/api/customers/guest-orders", { method: "POST", body: JSON.stringify({ tableSessionToken, tableNumber: scannedTableNumber }) }); setGuestOrders(payload.orders); }
      catch (error) { setCustomerMessage(error instanceof Error ? error.message : "Unable to refresh orders."); }
      finally { setCustomerOrdersRefreshing(false); }
      return;
    }
    setCustomerOrdersRefreshing(true);
    setCustomerMessage("");

    try {
      const payload = await apiRequest<{ customer: CustomerAccount; orders: Order[]; rewards?: CustomerRewards }>("/api/customers/orders", {
        method: "POST",
        cache: "no-store",
        body: JSON.stringify({ email: customerSession.customer.email, password: customerSession.credential })
      });
      setCustomerOrders(payload.orders);
      setCustomerSession((current) => (current ? { ...current, customer: payload.customer } : null));
      if (payload.rewards) setCustomerRewards(payload.rewards);
      setCustomerMessage("Orders refreshed.");
    } catch (error) {
      setCustomerMessage(error instanceof Error ? error.message : "Unable to refresh orders.");
    } finally {
      setCustomerOrdersRefreshing(false);
    }
  }

  async function refreshCustomerRewards() {
    if (!customerSession) return;
    try {
      const payload = await apiRequest<{ rewards: CustomerRewards }>("/api/customers/rewards", {
        method: "POST",
        body: JSON.stringify({ email: customerSession.customer.email, password: customerSession.credential })
      });
      setCustomerRewards(payload.rewards);
    } catch (error) {
      setCustomerMessage(error instanceof Error ? error.message : "Unable to refresh rewards.");
    }
  }

  async function mutateAdmin<T>(operation: () => Promise<T>, successMessage: string) {
    setAdminMessage("");

    try {
      await operation();
      await Promise.all([refreshAdmin(), refreshPublic(), customerSession ? refreshCustomerRewards() : Promise.resolve()]);
      setAdminMessage(successMessage);
      return true;
    } catch (error) {
      setAdminMessage(error instanceof Error ? error.message : "Admin action failed.");
      return false;
    }
  }

  async function uploadAdminImage(file: File, scope: "menu" | "combo" | "topbar" | "brand" | "gallery" | "artwork") {
    const result = await adminRequest<{ imageUrl: string }>(
      adminPin,
      `/api/admin/images?scope=${scope}&filename=${encodeURIComponent(file.name)}`,
      {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file
      }
    );
    return result.imageUrl;
  }

  async function submitCategory(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const editing = Boolean(categoryForm.id);
    const saved = await mutateAdmin(
      () =>
        adminRequest(adminPin, editing ? `/api/admin/categories/${categoryForm.id}` : "/api/admin/categories", {
          method: editing ? "PUT" : "POST",
          body: JSON.stringify(categoryForm)
        }),
      editing ? "Category updated." : "Category created."
    );
    if (saved) {
      setCategoryForm(emptyCategoryForm);
      setCategoryDialogOpen(false);
    }
  }

  async function submitMenuItem(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const editing = Boolean(menuForm.id);
    const nutrition = {
      calories: Number(menuForm.calories),
      carbs: Number(menuForm.carbs),
      protein: Number(menuForm.protein),
      fat: Number(menuForm.fat),
      fiber: Number(menuForm.fiber)
    };

    const saved = await mutateAdmin(
      () =>
        adminRequest(adminPin, editing ? `/api/admin/menu-items/${menuForm.id}` : "/api/admin/menu-items", {
          method: editing ? "PUT" : "POST",
          body: JSON.stringify({
            ...menuForm,
            price: Number(menuForm.price),
            ingredients: menuForm.ingredientsText,
            nutrition,
            rating: Number(menuForm.rating),
            ratingCount: Number(menuForm.ratingCount)
          })
        }),
      editing ? "Menu item updated." : "Menu item created."
    );
    if (saved) {
      setMenuForm(emptyMenuForm);
      setMenuDialogOpen(false);
    }
  }

  async function submitCombo(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const editing = Boolean(comboForm.id);
    const items = Object.entries(comboSelections)
      .filter(([, quantity]) => quantity > 0)
      .map(([menuItemId, quantity]) => ({ menuItemId, quantity }));

    const success = await mutateAdmin(
      () =>
        adminRequest(adminPin, editing ? `/api/admin/combos/${comboForm.id}` : "/api/admin/combos", {
          method: editing ? "PUT" : "POST",
          body: JSON.stringify({
            ...comboForm,
            price: Number(comboForm.price),
            rating: Number(comboForm.rating),
            ratingCount: Number(comboForm.ratingCount),
            items
          })
        }),
      editing ? "Combo updated." : "Combo created."
    );
    if (success) {
      setComboForm(emptyComboForm);
      setComboSelections({});
    }
    return success;
  }

  async function submitDeliveryArea(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const editing = Boolean(deliveryAreaForm.id);
    const saved = await mutateAdmin(
      () =>
        adminRequest(
          adminPin,
          editing ? `/api/admin/delivery-areas/${deliveryAreaForm.id}` : "/api/admin/delivery-areas",
          {
            method: editing ? "PUT" : "POST",
            body: JSON.stringify({ ...deliveryAreaForm, charge: Number(deliveryAreaForm.charge) })
          }
        ),
      editing ? "Delivery area updated." : "Delivery area created."
    );
    if (saved) setDeliveryAreaForm(emptyDeliveryAreaForm);
    return saved;
  }

  async function confirmOrderChanges(original: Order, draft: Order) {
    for (const item of draft.items) {
      if (item.status !== original.items.find((entry) => entry.id === item.id)?.status) {
        await adminRequest(adminPin, `/api/admin/orders/${draft.id}/items/${item.id}`, {
          method: "PATCH", body: JSON.stringify({ status: item.status })
        });
      }
    }
    await adminRequest(adminPin, `/api/admin/orders/${draft.id}/status`, {
      method: "PATCH",
      body: JSON.stringify({
        status: draft.status,
        additionalDiscountCents: draft.additionalDiscountCents,
        ...(draft.status === "processing" ? { readyAt: draft.readyAt } : {})
      })
    });
    await Promise.all([refreshAdmin(), refreshPublic(), customerSession ? refreshCustomerRewards() : Promise.resolve()]);
    setAdminMessage(`Order ${draft.orderNumber} updated.`);
  }

  async function downloadHistory(format: "csv" | "pdf") {
    setAdminMessage("");
    const params = new URLSearchParams({
      from: historyRange.from,
      to: historyRange.to,
      format
    });

    try {
      const response = await fetch(`/api/admin/orders/history?${params.toString()}`, {
        headers: { "X-Admin-Pin": adminPin }
      });
      if (!response.ok) {
        const error = (await response.json().catch(() => ({ error: "Download failed." }))) as { error?: string };
        throw new Error(error.error ?? "Download failed.");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `digital-cafe-orders-${historyRange.from}-to-${historyRange.to}.${format === "csv" ? "csv" : "pdf"}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setAdminMessage(`${format === "csv" ? "Excel" : "PDF"} history download started.`);
    } catch (error) {
      setAdminMessage(error instanceof Error ? error.message : "Download failed.");
    }
  }

  async function saveSettings(event: FormEvent<HTMLFormElement>, section: SettingsSectionId) {
    event.preventDefault();
    let payload: Record<string, string | boolean>;
    try { payload = settingsSectionPayload(new FormData(event.currentTarget), section); }
    catch (error) { setAdminMessage(error instanceof Error ? error.message : "Unable to save settings."); return false; }
    if (section === "appearance") payload.ui_theme = normalizeUiTheme(String(payload.ui_theme));
    const saved = await mutateAdmin(
      () => adminRequest(adminPin, "/api/admin/settings", { method: "PUT", body: JSON.stringify(payload) }),
      settingsSectionLabels[section] + " saved."
    );
    if (saved && section === "appearance") setThemePreview(null);
    return saved;
  }

  const navItems: Array<{ id: View; label: string; icon: typeof Utensils }> = adminRoute
    ? [
        { id: "menu", label: "Menu", icon: Utensils },
        { id: "combos", label: "Combos", icon: BadgeDollarSign },
        { id: "admin", label: "Admin", icon: Settings }
      ]
    : [
        { id: "menu", label: "Menu", icon: Utensils },
        { id: "combos", label: "Combos", icon: BadgeDollarSign },
        { id: "cart", label: "Cart", icon: ShoppingCart },
        { id: "orders", label: "My Order", icon: ClipboardList },
        { id: "rewards", label: "My Reward", icon: Gift },
        { id: "gallery", label: "About us", icon: Camera }
      ];

  const authDialog = !adminRoute && authOpen ? (
        <AuthModal
          mode={authMode}
          form={authForm}
          setForm={setAuthForm}
          message={authMessage}
          busy={authBusy}
          googleClientId={bootstrap?.settings.google_client_id ?? ""}
          onGoogle={signInGoogle}
          verifiedEmail={authVerification?.email ?? ""}
          onMode={(mode) => {
            setAuthMode(mode);
            setAuthMessage("");
          }}
          onSubmit={submitAuth}
          onClose={() => {
            if (!authBusy) {
              setAuthOpen(false);
              setDeliveryAuthPending(false);
            }
          }}
        />
      ) : null;
  if (!adminRoute && tableQrPresent && tableEntry !== "available") {
    return <main className="table-entry-screen"><section className="surface table-entry-message" role="status">
      <Armchair size={36} /><h1>{tableEntry === "checking" ? "Checking table availability..." : tableEntry === "occupied" ? `Table ${scannedTableNumber} is currently occupied` : "Unable to open this table"}</h1>
      <p>{tableEntry === "checking" ? "Please wait while we check your table." : tableEntryMessage}</p>
      {tableEntry !== "checking" ? <button className="primary-button" type="button" onClick={() => void checkTableEntry()}><RotateCcw size={17} /><span>Check again</span></button> : null}
      {tableEntry !== "checking" && !customerSession ? <button className="secondary-button table-owner-signin" type="button" onClick={() => openAuth("login")}><LogIn size={17} /><span>Already ordering here? Sign in</span></button> : null}
    </section>{authDialog}</main>;
  }

  return (
    <div className={`app-shell${adminRoute ? " admin-mode" : ""}${sidebarCollapsed ? " sidebar-collapsed" : ""}`}>
      <aside className="side-nav" aria-label="Primary navigation">
        <SidebarDecoration settings={settings} />
        <div className="brand-mark">
          <BrandLogo imageUrl={settings.brand_image_url ?? ""} name={settings.cafe_name ?? "Digital Cafe"} />
          <div>
            <strong>{settings.cafe_name ?? "Digital Cafe"}</strong>
            <span>Menu OS</span>
          </div>
        </div>
        <button className="sidebar-toggle" type="button" aria-expanded={!sidebarCollapsed} aria-controls="primary-nav-links" aria-label={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"} title={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"} onClick={() => {
          setSidebarCollapsed(!sidebarCollapsed);
          try { window.localStorage.setItem("digital-cafe-sidebar-collapsed", String(!sidebarCollapsed)); } catch { /* Navigation still works without storage. */ }
        }}>{sidebarCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}<span>{sidebarCollapsed ? "Expand" : "Collapse navigation"}</span></button>
        {!adminRoute ? (
          <div className="mobile-header-actions">
            <CustomerHeaderActions
              customer={customerSession?.customer ?? null}
              customerInitial={customerInitial}
              compact
              onAuth={openAuth}
              onProfile={openCustomerProfile}
              onRatings={showItemRatings ? () => setRatingsOpen(true) : undefined}
            />
          </div>
        ) : null}
        <nav id="primary-nav-links">
          {navItems.map((item) => (
            <NavButton
              key={item.id}
              active={activeView === item.id}
              icon={item.icon}
              label={item.label}
              onClick={() => setActiveView(item.id)}
              count={item.id === "cart" ? cartTotals.totalItems : undefined}
            />
          ))}
        </nav>
        <div className="nav-note">
          <Leaf size={18} />
          <span>{settings.service_note ?? "Fresh menu, real-time orders."}</span>
        </div>
      </aside>

      <main className="workspace">
        {!adminRoute ? (
          <header
            className={`topbar customer-topbar${topbarBackgroundImage ? " has-background-image" : ""}`}
            style={
              topbarBackgroundImage
                ? {
                    backgroundImage: `linear-gradient(90deg, rgba(9, 14, 22, 0.18), rgba(9, 14, 22, 0.66)), url(${JSON.stringify(topbarBackgroundImage)})`
                  }
                : undefined
            }
          >
            <CustomerHeaderActions
              customer={customerSession?.customer ?? null}
              customerInitial={customerInitial}
              onAuth={openAuth}
              onProfile={() => setProfileOpen(true)}
              onRatings={showItemRatings ? () => setRatingsOpen(true) : undefined}
            />
          </header>
        ) : null}

        {publicError ? <StatusBanner tone="error" message={publicError} /> : null}
        {orderMessage ? <StatusBanner tone="success" message={orderMessage} /> : null}

        {activeView === "menu" ? (
          <section className="surface menu-surface customer-catalog-surface">
            <div className="section-heading catalog-section-heading">
              <div className="section-heading-copy">
                <span className="section-title-icon" aria-hidden="true">
                  <Utensils size={18} strokeWidth={2.2} />
                </span>
                <div>
                  <h2>Food Catalog</h2>
                  <p>Browse food by category with ingredients, nutrition, ratings, and best seller markers.</p>
                </div>
              </div>
              <div className="catalog-heading-actions">
                <button
                  className={`icon-button mobile-filter-button${filtersAreActive ? " is-active" : ""}`}
                  type="button"
                  onClick={() => setFiltersOpen(true)}
                  aria-label="Open menu filters"
                  aria-haspopup="dialog"
                  aria-expanded={filtersOpen}
                  title="Filter menu"
                >
                  <SlidersHorizontal size={18} />
                </button>
                <button
                  className={`icon-button section-refresh-button${publicLoading ? " is-loading" : ""}`}
                  type="button"
                  onClick={() => void refreshPublic()}
                  disabled={publicLoading}
                  aria-label="Refresh food catalog"
                  title="Refresh food catalog"
                >
                  <RotateCcw size={18} />
                </button>
              </div>
            </div>

            {publicLoading ? (
              <div className="empty-state">Loading the cafe menu...</div>
            ) : (
              <div className="menu-grid">
                {filteredMenu.map((item) => (
                  <MenuCard
                    key={item.id}
                    item={item}
                    currency={currency}
                    onSelect={adminRoute ? undefined : () => selectItemRatings("menu", item.id, item.name)}
                    selected={selectedRatingItem?.itemType === "menu" && selectedRatingItem.itemId === item.id}
                    onAdd={adminRoute ? undefined : () => { setSelectedRatingItem({ itemType: "menu", itemId: item.id, name: item.name }); addToCart(item, "menu"); }}
                  />
                ))}
              </div>
            )}
          </section>
        ) : null}

        {activeView === "combos" ? (
          <section className="surface customer-catalog-surface">
            <div className="section-heading catalog-section-heading">
              <div className="section-heading-copy">
                <span className="section-title-icon" aria-hidden="true">
                  <BadgeDollarSign size={18} strokeWidth={2.2} />
                </span>
                <div>
                  <h2>Combo Offers</h2>
                  <p>Bundled offers show combined nutrition, ratings, best sellers, and savings against individual items.</p>
                </div>
              </div>
              <div className="catalog-heading-actions"><button className={`icon-button mobile-filter-button${comboFiltersAreActive ? " is-active" : ""}`} type="button" onClick={() => setFiltersOpen(true)} aria-label="Open combo filters" aria-haspopup="dialog" aria-expanded={filtersOpen} title="Filter combos"><SlidersHorizontal size={18} /></button><button
                className={`icon-button section-refresh-button${publicLoading ? " is-loading" : ""}`}
                type="button"
                onClick={() => void refreshPublic()}
                disabled={publicLoading}
                aria-label="Refresh combo offers"
                title="Refresh combo offers"
              >
                <RotateCcw size={18} />
              </button></div>
            </div>
            <div className="combo-grid">
              {visibleCombos.map((combo) => (
                <ComboCard
                  key={combo.id}
                  combo={combo}
                  currency={currency}
                  onSelect={adminRoute ? undefined : () => selectItemRatings("combo", combo.id, combo.name)}
                  selected={selectedRatingItem?.itemType === "combo" && selectedRatingItem.itemId === combo.id}
                  onAdd={adminRoute ? undefined : () => { setSelectedRatingItem({ itemType: "combo", itemId: combo.id, name: combo.name }); addToCart(combo, "combo"); }}
                />
              ))}
              {!publicLoading && !visibleCombos.length ? <div className="empty-state">No combos match your filters.</div> : null}
            </div>
          </section>
        ) : null}

        {activeView === "cart" ? (
          <section className="cart-page">
            <CartPanel
              cart={cart}
              totals={cartTotals}
              currency={currency}
              rewardBalance={rewardBalance}
              rewardPointsToUse={rewardPointsToUse}
              checkoutTotalCents={checkoutTotalCents}
              deliveryChargeCents={deliveryChargeCents}
              useRewardPoints={useRewardPoints}
              setUseRewardPoints={setUseRewardPoints}
              onQuantity={changeCartQuantity}
              checkoutForm={customer}
              setCheckoutForm={setCustomer}
              tableOptions={tableOptions}
              mapsApiKey={bootstrap?.settings.google_maps_api_key ?? ""}
              lockedTableNumber={scannedTableNumber}
              deliveryAreas={deliveryAreas}
              isGuestCheckout={isGuestCheckout}
              setIsGuestCheckout={setIsGuestCheckout}
              customerSession={customerSession}
              onSelectDelivery={selectOnlineDelivery}
              onRefresh={refreshCartItems}
              refreshing={cartRefreshing}
              onSubmit={placeOrder}
              submitting={submittingOrder}
            />
          </section>
        ) : null}

        {activeView === "orders" ? (
          <MyOrderPanel
            customerSession={customerSession}
            customerOrders={customerSession ? customerOrders : guestOrders}
            guestView={!customerSession && (guestOrders.length > 0 || Boolean(tableSessionToken))}
            onFeedback={setFeedbackOrder}
            customerMessage={customerMessage}
            onSignIn={() => openAuth("login")}
            onRefresh={refreshCustomerOrders}
            refreshing={customerOrdersRefreshing}
            currency={currency}
          />
        ) : null}

        {activeView === "rewards" ? (
          <RewardsPanel
            customerSession={customerSession}
            rewards={customerRewards}
            currency={currency}
            onRefresh={refreshCustomerRewards}
            onSignIn={() => openAuth("login")}
          />
        ) : null}

        {activeView === "gallery" ? <CafeGallery settings={settings} mapsApiKey={bootstrap?.settings.google_maps_api_key ?? ""} /> : null}

        {activeView === "admin" ? (
          <section className="surface admin-surface">
            <div className="admin-header">
              <div>
                <h2>Admin Dashboard</h2>
                <p>Configure menu data, combos, settings, feedback, and the order workflow.</p>
              </div>
              <form
                className="pin-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void refreshAdmin();
                }}
              >
                <input
                  type="password"
                  value={adminPin}
                  onChange={(event) => setAdminPin(event.target.value)}
                  placeholder="Admin PIN"
                  aria-label="Admin PIN"
                />
                <button className="primary-button" type="submit">
                  <Settings size={17} />
                  <span>Unlock</span>
                </button>
              </form>
            </div>

            {adminMessage ? <StatusBanner tone={adminMessage.includes("required") ? "error" : "success"} message={adminMessage} /> : null}
            {adminLoading ? <div className="empty-state">Loading admin dashboard...</div> : null}

            {adminData ? (
              <>
                <div className="admin-tabs" role="tablist" aria-label="Admin sections">
                  {adminTabs.map((tab) => {
                    const Icon = adminTabIcons[tab];
                    return (
                    <button
                      key={tab}
                      type="button"
                      className={activeAdminTab === tab ? "active" : ""}
                      aria-pressed={activeAdminTab === tab}
                      onClick={() => { setFocusedOrderId(null); setActiveAdminTab(tab); }}
                    >
                      <Icon size={18} aria-hidden="true" /><span>{adminTabLabels[tab]}</span>
                    </button>
                    );
                  })}
                </div>

                {activeAdminTab === "orders" ? (
                  <OrderBoard
                    orders={cafeOrders}
                    allOrders={adminData.orders.filter((order) => order.fulfillmentType === "cafe")}
                    currency={currency}
                    activeStatus={adminOrderStatusFilter}
                    onFilter={setAdminOrderStatusFilter}
                    onResetFilter={() => setAdminOrderStatusFilter("all")}
                    historyRange={historyRange}
                    setHistoryRange={setHistoryRange}
                    onDownload={downloadHistory}
                    onRefresh={() => void refreshAdmin()}
                    onConfirm={confirmOrderChanges}
                    rewardRate={Number(adminData.settings.reward_points_per_currency ?? 0)}
                    focusedOrderId={focusedOrderId}
                    onFocusConsumed={() => setFocusedOrderId(null)}
                  />
                ) : null}

                {activeAdminTab === "online-delivery" ? (
                  <div className="delivery-admin-stack">
                    <div className="online-delivery-heading">
                      <div>
                        <span>Total online orders</span>
                        <strong>{adminData.orders.filter((order) => order.fulfillmentType === "delivery").length}</strong>
                      </div>
                      <p>Manage online delivery orders through the same workflow used for cafe orders.</p>
                    </div>
                    <OrderBoard
                      orders={onlineDeliveryOrders}
                      allOrders={adminData.orders.filter((order) => order.fulfillmentType === "delivery")}
                      currency={currency}
                      activeStatus={adminOrderStatusFilter}
                      onFilter={setAdminOrderStatusFilter}
                      onResetFilter={() => setAdminOrderStatusFilter("all")}
                      historyRange={historyRange}
                      setHistoryRange={setHistoryRange}
                      onDownload={downloadHistory}
                      onRefresh={() => void refreshAdmin()}
                    onConfirm={confirmOrderChanges}
                      rewardRate={Number(adminData.settings.reward_points_per_currency ?? 0)}
                      focusedOrderId={focusedOrderId}
                      onFocusConsumed={() => setFocusedOrderId(null)}
                      deliveryOnly
                    />
                  </div>
                ) : null}

                {activeAdminTab === "delivery-status" ? (
                  <DeliveryAreaStatusBoard
                    deliveryAreas={adminData.deliveryAreas}
                    orders={adminData.orders.filter((order) => order.fulfillmentType === "delivery")}
                    currency={currency}
                    dateRange={deliveryStatusRange}
                    setDateRange={setDeliveryStatusRange}
                    onRefresh={() => void refreshAdmin()}
                    onOpenOrder={(order) => {
                      setAdminOrderStatusFilter("all");
                      setFocusedOrderId(order.id);
                      setActiveAdminTab("online-delivery");
                      setAdminMessage(`Showing ${order.orderNumber} for ${order.deliveryAreaName}.`);
                    }}
                  />
                ) : null}

                {activeAdminTab === "tables" ? (
                  <TableOccupancyBoard
                    tables={adminData.tables}
                    onRefresh={() => void refreshAdmin()}
                    onOpenOrder={(order) => {
                      setFocusedOrderId(order.id);
                      setAdminOrderStatusFilter(order.status);
                      setActiveAdminTab("orders");
                      setAdminMessage(`Showing ${order.orderNumber} from table view.`);
                    }}
                  />
                ) : null}

                {activeAdminTab === "catalog" ? (
                  <div className="admin-catalog-stack">
                    <CategoryEditor
                      categories={adminData.categories}
                      onAdd={() => {
                        setCategoryForm(emptyCategoryForm);
                        setCategoryDialogOpen(true);
                      }}
                      onEdit={(category) => {
                        setCategoryForm({ ...category, sortOrder: category.sortOrder });
                        setCategoryDialogOpen(true);
                      }}
                      onDeactivate={(id) =>
                        void mutateAdmin(
                          () => adminRequest(adminPin, `/api/admin/categories/${id}`, { method: "DELETE" }),
                          "Category deactivated."
                        )
                      }
                    />
                    <MenuEditor
                      menuItems={adminData.menuItems}
                      currency={currency}
                      onAdd={() => {
                        setMenuForm(emptyMenuForm);
                        setMenuDialogOpen(true);
                      }}
                      onEdit={(item) => {
                        setMenuForm(menuFormFromItem(item));
                        setMenuDialogOpen(true);
                      }}
                      onDeactivate={(id) =>
                        void mutateAdmin(
                          () => adminRequest(adminPin, `/api/admin/menu-items/${id}`, { method: "DELETE" }),
                          "Menu item deactivated."
                        )
                      }
                    />
                    <ComboEditor
                      form={comboForm}
                      setForm={setComboForm}
                      selections={comboSelections}
                      setSelections={setComboSelections}
                      menuItems={adminData.menuItems}
                      combos={adminData.combos}
                      currency={currency}
                      onImageUpload={(file) => uploadAdminImage(file, "combo")}
                      onSubmit={submitCombo}
                      onDeactivate={(id) =>
                        void mutateAdmin(
                          () => adminRequest(adminPin, `/api/admin/combos/${id}`, { method: "DELETE" }),
                          "Combo deactivated."
                        )
                      }
                    />
                  </div>
                ) : null}

                {activeAdminTab === "feedback" ? <FeedbackList feedback={adminData.feedback} /> : null}

                {activeAdminTab === "settings" ? (
                  <div className="settings-stack">
                    <SettingsForm
                      settings={adminData.settings}
                      onImageUpload={(file) => uploadAdminImage(file, "topbar")}
                      onBrandUpload={(file) => uploadAdminImage(file, "brand")}
                      onGalleryUpload={(file) => uploadAdminImage(file, "gallery")}
                      onArtworkUpload={(file) => uploadAdminImage(file, "artwork")}
                      mapsApiKey={bootstrap?.settings.google_maps_api_key ?? ""}
                      onSubmit={saveSettings}
                      onThemePreview={setThemePreview}
                    />
                    <DeliveryAreaEditor
                      mapsApiKey={bootstrap?.settings.google_maps_api_key ?? ""}
                      form={deliveryAreaForm}
                      setForm={setDeliveryAreaForm}
                      deliveryAreas={adminData.deliveryAreas}
                      currency={currency}
                      onSubmit={submitDeliveryArea}
                    />
                  </div>
                ) : null}

                {categoryDialogOpen ? (
                  <CategoryDialog
                    form={categoryForm}
                    setForm={setCategoryForm}
                    onSubmit={submitCategory}
                    onClose={() => {
                      setCategoryDialogOpen(false);
                      setCategoryForm(emptyCategoryForm);
                    }}
                  />
                ) : null}

                {menuDialogOpen ? (
                  <MenuItemDialog
                    form={menuForm}
                    setForm={setMenuForm}
                    categories={adminData.categories}
                    onImageUpload={(file) => uploadAdminImage(file, "menu")}
                    onSubmit={submitMenuItem}
                    onClose={() => {
                      setMenuDialogOpen(false);
                      setMenuForm(emptyMenuForm);
                    }}
                  />
                ) : null}
              </>
            ) : (
              <div className="empty-state">Enter the admin PIN to manage the cafe configuration.</div>
            )}
          </section>
        ) : null}
      </main>

      {!adminRoute ? (
        <aside className="cart-rail" aria-label="Cart summary">
          <CartSummary cart={cart} totals={cartTotals} currency={currency} onQuantity={changeCartQuantity} onOpen={() => setActiveView("cart")} />
          {showItemRatings ? <CustomerRatingsRail feedback={scopedFeedback} context={ratingsContext} /> : null}
        </aside>
      ) : null}

      {!adminRoute && feedbackOrder ? <OrderFeedbackDialog key={feedbackOrder.id} order={feedbackOrder} onSubmit={submitOrderFeedback} onClose={() => setFeedbackOrder(null)} /> : null}

      {orderSuccess ? <div className="modal-layer"><button className="modal-backdrop" type="button" onClick={() => setOrderSuccess(null)} aria-label="Close order confirmation" /><section className="auth-modal order-success-dialog" role="dialog" aria-modal="true" aria-labelledby="order-success-title"><PackageCheck size={44} /><h2 id="order-success-title">Order placed successfully</h2><p>{orderSuccess.orderNumber}</p><strong>{money(orderSuccess.totalCents, currency)}</strong><p>{orderSuccess.fulfillmentType === "delivery" ? "Your delivery order has been received." : `Your order for table ${orderSuccess.tableNumber} has been received.`}</p><button className="primary-button full" type="button" onClick={() => { setOrderSuccess(null); setActiveView("orders"); }}>View My Order</button></section></div> : null}
      {authDialog}

      {!adminRoute && profileOpen && customerSession ? (
        <CustomerProfileDrawer
          customer={customerSession.customer}
          mapsApiKey={bootstrap?.settings.google_maps_api_key ?? ""}
          saving={profileSaving}
          form={profileForm}
          setForm={setProfileForm}
          deliveryAreas={deliveryAreas}
          message={profileMessage}
          onSubmit={saveCustomerProfile}
          onClose={() => { if (!profileSaving) setProfileOpen(false); }}
          onSignOut={signOutCustomer}
        />
      ) : null}

      {showItemRatings && ratingsOpen ? (
        <>
          <button className="ratings-drawer-backdrop" type="button" onClick={() => setRatingsOpen(false)} aria-label="Close customer ratings" />
          <aside className="ratings-drawer" aria-label="Customer ratings">
            <button className="drawer-close" type="button" onClick={() => setRatingsOpen(false)} aria-label="Close customer ratings">
              <X size={20} />
            </button>
            <CustomerRatingsRail feedback={scopedFeedback} context={ratingsContext} expanded />
          </aside>
        </>
      ) : null}

      {!adminRoute && filtersOpen ? (
        <MenuFilterDialog
          sort={activeView === "combos" ? comboSort : menuSort}
          setSort={activeView === "combos" ? setComboSort : setMenuSort}
          hideCategories={activeView === "combos"}
          searchPlaceholder={activeView === "combos" ? "Search combos and included items" : undefined}
          categories={categories}
          categoryCounts={categoryCounts}
          search={activeView === "combos" ? comboSearch : search}
          setSearch={activeView === "combos" ? setComboSearch : setSearch}
          categoryId={categoryId}
          setCategoryId={setCategoryId}
          maxPriceCents={activeView === "combos" ? comboMaxPrice : maxPriceCents}
          setMaxPriceCents={activeView === "combos" ? setComboPriceLimit : setMaxPriceCents}
          highestPriceCents={activeView === "combos" ? highestComboPrice : highestPriceCents}
          currency={currency}
          onClear={() => {
            if (activeView === "combos") { setComboSearch(""); setComboPriceLimit(null); setComboSort("price-asc"); return; }
            setSearch("");
            setCategoryId("all");
            setMaxPriceCents(highestPriceCents);
            setMenuSort("price-asc");
          }}
          onClose={() => setFiltersOpen(false)}
        />
      ) : null}

      <nav className={adminRoute ? "mobile-nav admin-mobile-nav" : "mobile-nav"} aria-label="Mobile navigation">
        {navItems.map((item) => {
          const Icon = item.icon;
          const cartCount = item.id === "cart" ? cartTotals.totalItems : undefined;
          return (
            <button key={item.id} type="button" className={activeView === item.id ? "active" : ""} onClick={() => setActiveView(item.id)}>
              <Icon size={19} />
              <span>{item.label}</span>
              {typeof cartCount === "number" ? (
                <strong className="mobile-nav-count" aria-label={`${cartCount} items in cart`}>
                  {cartCount > 99 ? "99+" : cartCount}
                </strong>
              ) : null}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function CustomerHeaderActions({
  customer,
  customerInitial,
  compact = false,
  onAuth,
  onProfile,
  onRatings
}: {
  customer: CustomerAccount | null;
  customerInitial: string;
  compact?: boolean;
  onAuth: (mode: AuthMode) => void;
  onProfile: () => void;
  onRatings?: () => void;
}) {
  return (
    <div className={`customer-header-actions${compact ? " compact" : ""}`}>
      {onRatings ? <button
        className="header-rating-button"
        type="button"
        onClick={onRatings}
        aria-label="Open customer ratings"
        title="Customer ratings"
      >
        <Star size={17} fill="currentColor" />
      </button> : null}
      {customer ? (
        <button className="customer-account-chip" type="button" onClick={onProfile} aria-label="Open My Profile">
          <span className="profile-avatar header-avatar">{customerInitial}</span>
          <span className="customer-account-copy">
            <small>Signed in as</small>
            <strong>{customer.email}</strong>
          </span>
        </button>
      ) : (
        <div className="auth-quick-actions">
          <button className="header-auth-button secondary" type="button" onClick={() => onAuth("signup")}>
            <UserPlus size={15} />
            <span>Sign up</span>
          </button>
          <button className="header-auth-button primary" type="button" onClick={() => onAuth("login")}>
            <LogIn size={15} />
            <span>Sign in</span>
          </button>
        </div>
      )}
    </div>
  );
}

function AuthModal({
  googleClientId,
  onGoogle,
  mode,
  form,
  setForm,
  message,
  busy,
  verifiedEmail,
  onMode,
  onSubmit,
  onClose
}: {
  googleClientId: string;
  onGoogle: (credential: string, nonce: string) => Promise<void>;
  mode: AuthMode;
  form: { email: string; password: string; newPassword: string; confirmPassword: string };
  setForm: (value: { email: string; password: string; newPassword: string; confirmPassword: string }) => void;
  message: string;
  busy: boolean;
  verifiedEmail: string;
  onMode: (mode: AuthMode) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
}) {
  const heading =
    mode === "login"
      ? "Welcome back"
      : mode === "signup"
        ? "Create your account"
        : mode === "reset"
          ? "Reset your password"
          : "Choose a new password";

  return (
    <div className="modal-layer">
      <button className="modal-backdrop" type="button" onClick={onClose} aria-label="Close sign in" />
      <section className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
        <button className="drawer-close" type="button" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>
        <div className="auth-modal-brand">
          <span><ChefHat size={22} /></span>
          <div>
            <small>Digital Cafe account</small>
            <h2 id="auth-modal-title">{heading}</h2>
          </div>
        </div>
        <p>
          {mode === "login"
            ? "Sign in to see your order history, rewards, and saved delivery details."
            : mode === "set-password"
              ? `${verifiedEmail || "Your email"} is verified. Use at least 8 characters.`
              : "We’ll email you a secure link that is valid for 30 minutes."}
        </p>

        {googleClientId && (mode === "login" || mode === "signup") ? <><GoogleSignIn clientId={googleClientId} onCredential={onGoogle} busy={busy} /><div className="auth-method-divider"><span>or</span></div></> : null}
        <form className="auth-modal-form" onSubmit={onSubmit}>
          {mode !== "set-password" ? (
            <label>
              <span>Email address</span>
              <input
                autoFocus
                required
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
                placeholder="you@example.com"
              />
            </label>
          ) : null}
          {mode === "login" ? (
            <label>
              <span>Password</span>
              <input
                required
                type="password"
                autoComplete="current-password"
                value={form.password}
                onChange={(event) => setForm({ ...form, password: event.target.value })}
                placeholder="Your password"
              />
            </label>
          ) : null}
          {mode === "set-password" ? (
            <>
              <label>
                <span>New password</span>
                <input
                  autoFocus
                  required
                  minLength={8}
                  type="password"
                  autoComplete="new-password"
                  value={form.newPassword}
                  onChange={(event) => setForm({ ...form, newPassword: event.target.value })}
                  placeholder="At least 8 characters"
                />
              </label>
              <label>
                <span>Confirm new password</span>
                <input
                  required
                  minLength={8}
                  type="password"
                  autoComplete="new-password"
                  value={form.confirmPassword}
                  onChange={(event) => setForm({ ...form, confirmPassword: event.target.value })}
                  placeholder="Enter it again"
                />
              </label>
            </>
          ) : null}
          {message ? <div className="auth-message" role="status">{message}</div> : null}
          <button className="primary-button full" type="submit" disabled={busy}>
            {mode === "login" ? <LogIn size={18} /> : <Mail size={18} />}
            <span>
              {busy
                ? "Please wait..."
                : mode === "login"
                  ? "Sign in"
                  : mode === "signup"
                    ? "Send sign-up link"
                    : mode === "reset"
                      ? "Send reset link"
                      : "Save password & sign in"}
            </span>
          </button>
        </form>

        {mode === "login" ? (
          <div className="auth-links">
            <button type="button" onClick={() => onMode("signup")}>Sign up for a new account</button>
            <button type="button" onClick={() => onMode("reset")}>Forgot password?</button>
          </div>
        ) : mode !== "set-password" ? (
          <div className="auth-links single">
            <button type="button" onClick={() => onMode("login")}>Back to sign in</button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function CustomerProfileDrawer({
  mapsApiKey,
  saving,
  customer,
  form,
  setForm,
  deliveryAreas,
  message,
  onSubmit,
  onClose,
  onSignOut
}: {
  mapsApiKey: string;
  saving: boolean;
  customer: CustomerAccount;
  form: ProfileForm;
  setForm: (value: ProfileForm) => void;
  deliveryAreas: DeliveryArea[];
  message: string;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
  onSignOut: () => void;
}) {
  const selectedArea = deliveryAreas.find(area => area.id === form.deliveryAreaId);
  const hasLocation = Boolean(form.deliveryAreaId || form.deliveryLatitude != null || form.deliveryLongitude != null);
  const locationError = hasLocation ? (selectedArea ? deliveryLocationError(selectedArea, form.deliveryLatitude, form.deliveryLongitude) : "Select an available delivery area for your address pin.") : "";
  return (
    <>
      <button className="profile-drawer-backdrop" type="button" disabled={saving} onClick={onClose} aria-label="Close customer profile" />
      <aside className="profile-drawer" role="dialog" aria-modal="true" aria-label="Customer profile">
        <button className="drawer-close" type="button" disabled={saving} onClick={onClose} aria-label="Close customer profile">
          <X size={20} />
        </button>
        <div className="profile-drawer-heading">
          <span className="profile-avatar large">{customer.name.slice(0, 1).toUpperCase()}</span>
          <div>
            <small>Signed in as</small>
            <strong>{customer.email}</strong>
          </div>
        </div>
        <h2>Customer profile</h2>
        <p>Keep these details current so delivery checkout is fast and accurate.</p>
        <form className="profile-form" onSubmit={onSubmit}><fieldset className="combo-dialog-fields" disabled={saving}>
          <label>
            <span>Customer name</span>
            <input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </label>
          <label>
            <span>Mobile number</span>
            <input value={form.mobile} onChange={(event) => setForm({ ...form, mobile: event.target.value })} placeholder="Mobile number" />
          </label>
          <label>
            <span>Default delivery area</span>
            <select value={form.deliveryAreaId} onChange={(event) => setForm({ ...form, deliveryAreaId: event.target.value, ...(event.target.value ? {} : { deliveryLatitude: null, deliveryLongitude: null, deliveryPinnedAddress: "", deliveryAddress: "" }) })}>
              <option value="">No default delivery area</option>
              {form.deliveryAreaId && !selectedArea ? <option value={form.deliveryAreaId}>Saved delivery area unavailable</option> : null}
              {deliveryAreas.map((area) => (
                <option key={area.id} value={area.id}>{area.name}</option>
              ))}
            </select>
          </label>
          <DeliveryMap apiKey={mapsApiKey} label="Default delivery address" locateOnOpen={Boolean(form.deliveryAreaId)} boundary={selectedArea} point={form.deliveryLatitude != null && form.deliveryLongitude != null ? { latitude: form.deliveryLatitude, longitude: form.deliveryLongitude, address: form.deliveryPinnedAddress } : null} onChange={point => setForm({ ...form, deliveryLatitude: point.latitude, deliveryLongitude: point.longitude, deliveryPinnedAddress: point.address, deliveryAddress: point.address })} />
          {selectedArea ? <small>Choose an address within {selectedArea.radiusMiles} mile{selectedArea.radiusMiles === 1 ? "" : "s"} of {selectedArea.centerAddress || selectedArea.name}.</small> : <small>A default delivery location is optional. Select an area and pin an address to save one.</small>}
          {locationError ? <div className="status-banner error" role="alert">{locationError}</div> : selectedArea ? <div className="status-banner">This address is within the delivery area.</div> : null}
          {hasLocation || form.deliveryAddress ? <button type="button" className="secondary-button full" onClick={() => setForm({ ...form, deliveryAreaId: "", deliveryAddress: "", deliveryLatitude: null, deliveryLongitude: null, deliveryPinnedAddress: "" })}>Clear delivery location</button> : null}
          <label>
            <span>Full delivery address</span>
            <textarea
              required={Boolean(form.deliveryAreaId)}
              value={form.deliveryAddress}
              onChange={(event) => setForm({ ...form, deliveryAddress: event.target.value })}
              placeholder="Building, street, floor, and nearby landmark"
            />
          </label>
          </fieldset>
          {message ? <div className="auth-message" role="status">{message}</div> : null}
          <button className="primary-button full" type="submit" disabled={saving || Boolean(locationError)}>
            <Save size={18} />
            <span>{saving ? "Saving..." : "Save profile"}</span>
          </button>
          <button className="secondary-button full" type="button" disabled={saving} onClick={onSignOut}>
            <LogIn size={18} />
            <span>Sign out</span>
          </button>
        </form>
      </aside>
    </>
  );
}

function BrandLogo({ imageUrl, name }: { imageUrl: string; name: string }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  return imageUrl.trim() && failedUrl !== imageUrl ? <img className="brand-logo" src={imageUrl} alt={`${name} logo`} onError={() => setFailedUrl(imageUrl)} /> : <ChefHat size={24} aria-label="Default cafe logo" />;
}

function NavButton({
  active,
  icon: Icon,
  label,
  count,
  onClick
}: {
  active: boolean;
  icon: typeof Utensils;
  label: string;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button className={active ? "nav-button active" : "nav-button"} type="button" onClick={onClick} title={label} aria-label={label}>
      <Icon size={19} />
      <span>{label}</span>
      {typeof count === "number" ? <strong>{count}</strong> : null}
    </button>
  );
}

function StatusBanner({ tone, message }: { tone: "success" | "error"; message: string }) {
  return <div className={`status-banner ${tone}`}>{message}</div>;
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="mini-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function FilterBar({
  className = "",
  categories = [],
  categoryCounts = {},
  hideCategories = false,
  searchPlaceholder = "Search ingredients, meals, drinks",
  search,
  setSearch,
  categoryId = "all",
  setCategoryId,
  maxPriceCents,
  setMaxPriceCents,
  highestPriceCents,
  currency,
  sort,
  setSort
}: {
  className?: string;
  categories?: Category[];
  categoryCounts?: Record<string, number>;
  hideCategories?: boolean;
  searchPlaceholder?: string;
  search: string;
  setSearch: (value: string) => void;
  categoryId?: string;
  setCategoryId?: (value: string) => void;
  maxPriceCents: number;
  setMaxPriceCents: (value: number) => void;
  highestPriceCents: number;
  currency: string;
  sort: CatalogSort;
  setSort: (value: CatalogSort) => void;
}) {
  return (
    <div className={`filter-bar${className ? ` ${className}` : ""}`}>
      <label className="search-box">
        <Search size={18} />
        <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={searchPlaceholder} />
      </label>
      {!hideCategories ? <label className="select-box">
        <SlidersHorizontal size={18} />
        <select value={categoryId} onChange={(event) => setCategoryId?.(event.target.value)}>
          <option value="all">All categories ({categoryCounts.all ?? 0})</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name} ({categoryCounts[category.id] ?? 0})
            </option>
          ))}
        </select>
      </label> : null}
      <label className="range-box">
        <span>Up to {money(maxPriceCents, currency)}</span>
        <input
          type="range"
          min={500}
          max={highestPriceCents}
          step={50}
          value={maxPriceCents}
          onChange={(event) => setMaxPriceCents(Number(event.target.value))}
        />
      </label>
      <label className="select-box catalog-sort-box"><span>Sort</span><select aria-label={hideCategories ? "Sort combo offers" : "Sort menu items"} value={sort} onChange={event => setSort(event.target.value as CatalogSort)}>{catalogSortOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
    </div>
  );
}

function MenuFilterDialog({
  onClear,
  onClose,
  ...filterProps
}: Omit<Parameters<typeof FilterBar>[0], "className"> & {
  onClear: () => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-layer mobile-filter-layer">
      <button className="modal-backdrop" type="button" onClick={onClose} aria-label={filterProps.hideCategories ? "Close combo filters" : "Close menu filters"} />
      <section className="mobile-filter-dialog" role="dialog" aria-modal="true" aria-labelledby="mobile-filter-title">
        <div className="mobile-filter-heading">
          <div>
            <span>Discover your next order</span>
            <h2 id="mobile-filter-title">{filterProps.hideCategories ? "Filter combos" : "Filter the menu"}</h2>
          </div>
          <button className="drawer-close inline" type="button" onClick={onClose} aria-label={filterProps.hideCategories ? "Close combo filters" : "Close menu filters"}>
            <X size={20} />
          </button>
        </div>
        <FilterBar {...filterProps} className="mobile-filter-controls" />
        <div className="mobile-filter-actions">
          <button className="secondary-button" type="button" onClick={onClear}>Clear</button>
          <button className="primary-button" type="button" onClick={onClose}>Show results</button>
        </div>
      </section>
    </div>
  );
}

function MenuCard({ item, currency, onAdd, onSelect, selected = false }: { item: MenuItem; currency: string; onAdd?: () => void; onSelect?: () => void; selected?: boolean }) {
  return (
    <article className="menu-card">
      <div className="dish-image">
        <img src={item.imageUrl || fallbackImages.menu} alt={item.name} loading="lazy" />
        {item.isBestSeller ? (
          <span className="best-seller">
            <Flame size={14} />
            Best seller
          </span>
        ) : null}
      </div>
      <div className="card-body">
        <div className="card-title-row">
          <div>
            <span className="category-name">{item.categoryName}</span>
            <h3>{item.name}</h3>
          </div>
          <strong className="price">{money(item.priceCents, currency)}</strong>
        </div>
        {item.description ? <p>{item.description}</p> : null}
        <IngredientLine ingredients={item.ingredients} />
        <NutritionPills nutrition={item.nutrition} />
        <div className="card-footer">
          {onSelect ? <button className="item-rating-button" type="button" onClick={onSelect} aria-label={`View ratings for ${item.name}`} aria-pressed={selected}><Rating rating={item.rating} count={item.ratingCount} /></button> : <Rating rating={item.rating} count={item.ratingCount} />}
          {onAdd ? (
            <button className="primary-button small" type="button" onClick={onAdd}>
              <Plus size={16} />
              <span>Add</span>
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function ComboCard({ combo, currency, onAdd, onSelect, selected = false }: { combo: Combo; currency: string; onAdd?: () => void; onSelect?: () => void; selected?: boolean }) {
  return (
    <article className="combo-card">
      <div className="dish-image wide">
        <img src={combo.imageUrl || fallbackImages.combo} alt={combo.name} loading="lazy" />
        {combo.isBestSeller ? (
          <span className="best-seller">
            <Flame size={14} />
            Best seller
          </span>
        ) : null}
      </div>
      <div className="card-body">
        <div className="card-title-row">
          <div>
            <span className="category-name">Combo offer</span>
            <h3>{combo.name}</h3>
          </div>
          <strong className="price">{money(combo.priceCents, currency)}</strong>
        </div>
        {combo.description ? <p>{combo.description}</p> : null}
        <div className="combo-items">
          {combo.items.map(({ item, quantity }) => (
            <span key={item.id}>
              {quantity}x {item.name}
            </span>
          ))}
        </div>
        <div className="savings-row">
          <span>Individual total {money(combo.individualPriceCents, currency)}</span>
          <strong>Save {money(combo.savingsCents, currency)}</strong>
        </div>
        <NutritionPills nutrition={combo.totalNutrition} />
        <div className="card-footer">
          {onSelect ? <button className="item-rating-button" type="button" onClick={onSelect} aria-label={`View ratings for ${combo.name}`} aria-pressed={selected}><Rating rating={combo.rating} count={combo.ratingCount} /></button> : <Rating rating={combo.rating} count={combo.ratingCount} />}
          {onAdd ? (
            <button className="primary-button small" type="button" onClick={onAdd}>
              <Plus size={16} />
              <span>Add combo</span>
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function IngredientLine({ ingredients }: { ingredients: string[] }) {
  return (
    <div className="ingredients">
      {ingredients.slice(0, 5).map((ingredient) => (
        <span key={ingredient}>{ingredient}</span>
      ))}
    </div>
  );
}

function NutritionPills({ nutrition }: { nutrition: Nutrition }) {
  const entries = Object.entries(nutrition).slice(0, 5);
  return (
    <div className="nutrition-pills">
      {entries.map(([key, value]) => (
        <span key={key}>
          {key} <strong>{value}</strong>
        </span>
      ))}
    </div>
  );
}

function Rating({ rating, count }: { rating: number; count: number }) {
  return (
    <div className="rating">
      <Star size={16} fill="currentColor" />
      <strong>{rating.toFixed(1)}</strong>
      <span>{count} ratings</span>
    </div>
  );
}

function CartSummary({
  cart,
  totals,
  currency,
  onQuantity,
  onOpen
}: {
  cart: CartItem[];
  totals: { totalItems: number; totalCents: number; nutrition: Nutrition };
  currency: string;
  onQuantity: (key: string, delta: number) => void;
  onOpen: () => void;
}) {
  return (
    <div className="rail-panel">
      <div className="rail-header">
        <div>
          <span>My Cart</span>
          <strong>{totals.totalItems} orders</strong>
        </div>
        <button className="icon-button" type="button" onClick={onOpen} title="Open cart">
          <ShoppingCart size={18} />
        </button>
      </div>
      <div className="rail-items">
        {cart.length === 0 ? (
          <div className="empty-cart">Cart is waiting for something delicious.</div>
        ) : (
          cart.map((item) => (
            <div className="cart-mini" key={item.key}>
              <img src={item.imageUrl} alt="" />
              <div>
                <strong>{item.name}</strong>
                <span>{money(item.unitPriceCents * item.quantity, currency)}</span>
                {item.details.length ? <small>{item.details.join(", ")}</small> : null}
              </div>
              <Stepper value={item.quantity} onMinus={() => onQuantity(item.key, -1)} onPlus={() => onQuantity(item.key, 1)} />
            </div>
          ))
        )}
      </div>
      <div className="rail-total">
        <span>Total</span>
        <strong>{money(totals.totalCents, currency)}</strong>
      </div>
      <button className="primary-button full" type="button" onClick={onOpen}>
        <CreditCard size={18} />
        <span>Place order</span>
      </button>
    </div>
  );
}

function CustomerRatingsRail({ feedback, context = "", expanded = false }: { feedback: Feedback[]; context?: string; expanded?: boolean }) {
  const recentFeedback = [...feedback]
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
    .slice(0, expanded ? feedback.length : 5);

  return (
    <div className={expanded ? "rail-panel ratings-rail expanded" : "rail-panel ratings-rail"}>
      <div className="rail-header">
        <div>
          <span>Customer Ratings</span>
          <strong>{feedback.length} reviews</strong>
        </div>
        <Star size={18} fill="currentColor" />
      </div>
      {context ? <p className="ratings-context">{context}</p> : null}
      <div className="rating-card-list">
        {recentFeedback.length === 0 ? (
          <div className="empty-cart">No reviews for these items yet.</div>
        ) : (
          recentFeedback.map((entry) => (
            <article className="rating-mini-card" key={entry.id}>
              <div>
                <strong>{entry.customerName || "Guest"}</strong>
                <span>
                  <Star size={14} fill="currentColor" /> {entry.rating}/5
                </span>
              </div>
              {entry.itemName ? <small className="review-item-name">{entry.itemName}</small> : null}
              <p>{entry.message || "Rated this item."}</p>
              <small>{new Date(entry.createdAt).toLocaleDateString()}</small>
            </article>
          ))
        )}
      </div>
    </div>
  );
}

function CartPanel({
  cart,
  totals,
  currency,
  rewardBalance,
  rewardPointsToUse,
  checkoutTotalCents,
  deliveryChargeCents,
  useRewardPoints,
  setUseRewardPoints,
  onQuantity,
  checkoutForm,
  setCheckoutForm,
  tableOptions,
  mapsApiKey,
  lockedTableNumber,
  deliveryAreas,
  isGuestCheckout,
  setIsGuestCheckout,
  customerSession,
  onSelectDelivery,
  onRefresh,
  refreshing,
  onSubmit,
  submitting
}: {
  cart: CartItem[];
  totals: { totalItems: number; totalCents: number; nutrition: Nutrition };
  currency: string;
  rewardBalance: number;
  rewardPointsToUse: number;
  checkoutTotalCents: number;
  deliveryChargeCents: number;
  useRewardPoints: boolean;
  setUseRewardPoints: (value: boolean) => void;
  onQuantity: (key: string, delta: number) => void;
  checkoutForm: CheckoutForm;
  setCheckoutForm: (value: CheckoutForm) => void;
  tableOptions: string[];
  mapsApiKey: string;
  lockedTableNumber: string;
  deliveryAreas: DeliveryArea[];
  isGuestCheckout: boolean;
  setIsGuestCheckout: (value: boolean) => void;
  customerSession: CustomerSession | null;
  onSelectDelivery: () => void;
  onRefresh: () => Promise<void>;
  refreshing: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  submitting: boolean;
}) {
  const selectedArea = deliveryAreas.find(area => area.id === checkoutForm.deliveryAreaId);
  const locationError = checkoutForm.fulfillmentType === "delivery" ? (selectedArea ? deliveryLocationError(selectedArea, checkoutForm.deliveryLatitude, checkoutForm.deliveryLongitude) : "Select a delivery area to see its allowed radius.") : "";
  return (
    <section className="surface cart-panel">
      <div className="section-heading cart-panel-heading">
        <div className="section-heading-copy">
          <span className="section-title-icon" aria-hidden="true">
            <ShoppingCart size={18} strokeWidth={2.2} />
          </span>
          <div>
            <h2>My Cart</h2>
            <p>Review your items and complete your order.</p>
          </div>
        </div>
        <strong className="grand-total">{money(checkoutTotalCents, currency)}</strong>
        <button
          className={`icon-button section-refresh-button${refreshing ? " is-loading" : ""}`}
          type="button"
          onClick={() => void onRefresh()}
          disabled={refreshing}
          title="Refresh cart items"
          aria-label="Refresh cart items"
        >
          <RotateCcw size={18} />
        </button>
      </div>

      <div className="cart-lines">
        {cart.length === 0 ? (
          <div className="empty-state">No cart items yet. Add menu items or combo offers first.</div>
        ) : (
          cart.map((item) => (
            <div className="cart-line" key={item.key}>
              <img src={item.imageUrl} alt="" />
              <div>
                <strong>{item.name}</strong>
                <span>{money(item.unitPriceCents, currency)} each</span>
                {item.details.length ? <small>{item.details.join(", ")}</small> : null}
              </div>
              <Stepper value={item.quantity} onMinus={() => onQuantity(item.key, -1)} onPlus={() => onQuantity(item.key, 1)} />
              <strong>{money(item.unitPriceCents * item.quantity, currency)}</strong>
            </div>
          ))
        )}
      </div>

      <NutritionPills nutrition={totals.nutrition} />

      <form className="checkout-form" onSubmit={onSubmit}>
        <div className="fulfillment-choice" role="radiogroup" aria-label="How would you like your order?">
          {!lockedTableNumber ? (
          <button
            className={checkoutForm.fulfillmentType === "delivery" ? "active" : ""}
            type="button"
            onClick={onSelectDelivery}
          >
            <Truck size={20} />
            <span>
              <strong>Online Delivery</strong>
              <small>{customerSession ? "Delivered to your address" : "Sign in required"}</small>
            </span>
          </button>
          ) : null}
          {lockedTableNumber ? (
          <button
            className={checkoutForm.fulfillmentType === "cafe" ? "active" : ""}
            type="button"
            onClick={() => setCheckoutForm({ ...checkoutForm, fulfillmentType: "cafe" })}
          >
            <Store size={20} />
            <span>
              <strong>Eat at Cafe</strong>
              <small>Order for your table</small>
            </span>
          </button>
          ) : null}
        </div>

        {customerSession ? (
          <div className="reward-checkout">
            <div className="reward-breakdown">
              <div>
                <span>Available Reward Points</span>
                <strong>{rewardBalance}</strong>
              </div>
              <label>
                <span>Use Reward Points</span>
                <span className="reward-use-control">
                  <input
                    type="checkbox"
                    checked={useRewardPoints}
                    disabled={rewardBalance <= 0 || totals.totalCents <= 0}
                    onChange={(event) => setUseRewardPoints(event.target.checked)}
                  />
                  <strong>{rewardPointsToUse}</strong>
                </span>
              </label>
              {checkoutForm.fulfillmentType === "delivery" ? (
                <div>
                  <span>Delivery charge</span>
                  <strong>{money(deliveryChargeCents, currency)}</strong>
                </div>
              ) : null}
              <div className="net-payable-row">
                <span>Net Payable</span>
                <strong>{money(checkoutTotalCents, currency)}</strong>
              </div>
            </div>
          </div>
        ) : null}

        {checkoutForm.fulfillmentType === "cafe" ? (
          <div className="checkout-mode">
            <label className="check-line">
              <input
                type="checkbox"
                checked={isGuestCheckout && !customerSession}
                disabled={Boolean(customerSession)}
                onChange={(event) => setIsGuestCheckout(event.target.checked)}
              />
              Order as guest
            </label>
            {customerSession ? (
              <span className="session-chip">
                <KeyRound size={15} />
                Signed in as {customerSession.customer.name}
              </span>
            ) : (
              <span>Guest orders can be placed without customer details.</span>
            )}
          </div>
        ) : (
          <div className="delivery-note">
            <Truck size={18} />
            <span>Confirm the delivery details below. You may change them for this order without changing your saved profile.</span>
          </div>
        )}

        <FieldIcon icon={User}>
          <input
            required={checkoutForm.fulfillmentType === "delivery" || !isGuestCheckout || Boolean(customerSession)}
            disabled={checkoutForm.fulfillmentType === "cafe" && isGuestCheckout && !customerSession}
            value={checkoutForm.name}
            onChange={(event) => setCheckoutForm({ ...checkoutForm, name: event.target.value })}
            placeholder="Customer name"
          />
        </FieldIcon>
        <FieldIcon icon={Phone}>
          <input
            required={checkoutForm.fulfillmentType === "delivery" || !isGuestCheckout || Boolean(customerSession)}
            disabled={checkoutForm.fulfillmentType === "cafe" && isGuestCheckout && !customerSession}
            value={checkoutForm.mobile}
            onChange={(event) => setCheckoutForm({ ...checkoutForm, mobile: event.target.value })}
            placeholder="Mobile number"
          />
        </FieldIcon>
        <FieldIcon icon={Mail}>
          <input
            required={checkoutForm.fulfillmentType === "delivery" || !isGuestCheckout || Boolean(customerSession)}
            disabled={checkoutForm.fulfillmentType === "cafe" && isGuestCheckout && !customerSession}
            type="email"
            value={checkoutForm.email}
            onChange={(event) => setCheckoutForm({ ...checkoutForm, email: event.target.value })}
            placeholder="Email address"
          />
        </FieldIcon>

        {checkoutForm.fulfillmentType === "delivery" ? (
          <>
            <label className="select-box">
              <MapPin size={18} />
              <select
                required
                value={checkoutForm.deliveryAreaId}
                onChange={(event) => setCheckoutForm({ ...checkoutForm, deliveryAreaId: event.target.value })}
              >
                <option value="">Select delivery area</option>
                {deliveryAreas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name} — {money(area.chargeCents, currency)}
                  </option>
                ))}
              </select>
            </label>
            {deliveryAreas.length === 0 ? (
              <div className="status-banner error">Online delivery areas are being configured. Please try again later.</div>
            ) : null}
            <DeliveryMap apiKey={mapsApiKey} label="Delivery address" locateOnOpen boundary={selectedArea} point={checkoutForm.deliveryLatitude != null && checkoutForm.deliveryLongitude != null ? { latitude: checkoutForm.deliveryLatitude, longitude: checkoutForm.deliveryLongitude, address: checkoutForm.deliveryPinnedAddress } : null} onChange={point => setCheckoutForm({ ...checkoutForm, deliveryLatitude: point.latitude, deliveryLongitude: point.longitude, deliveryPinnedAddress: point.address, deliveryAddress: point.address })} />
            {selectedArea ? <small>Delivery within {selectedArea.radiusMiles} mile{selectedArea.radiusMiles === 1 ? "" : "s"} of {selectedArea.centerAddress || selectedArea.name}.</small> : null}
            {locationError ? <div className="status-banner error" role="alert">{locationError}</div> : <div className="status-banner">This address is within the delivery area.</div>}
            <textarea
              required
              value={checkoutForm.deliveryAddress}
              onChange={(event) => setCheckoutForm({ ...checkoutForm, deliveryAddress: event.target.value })}
              placeholder="Delivery address, building, floor, and helpful landmarks"
            />
            {!customerSession ? (
              <div className="delivery-fee-line">
                <span>Delivery charge</span>
                <strong>{money(deliveryChargeCents, currency)}</strong>
              </div>
            ) : null}
          </>
        ) : (
          <div className="checkout-table-field">
            <label className={`select-box checkout-table-select${lockedTableNumber ? " is-locked" : ""}`}>
              <Hash size={18} />
              <select
                required={tableOptions.length > 0}
                disabled={Boolean(lockedTableNumber)}
                value={checkoutForm.tableNumber}
                onChange={(event) => setCheckoutForm({ ...checkoutForm, tableNumber: event.target.value })}
                aria-label={lockedTableNumber ? `Table ${lockedTableNumber} selected from QR code` : "Select table number"}
              >
                <option value="">Select table no.</option>
                {checkoutForm.tableNumber && !tableOptions.includes(checkoutForm.tableNumber) ? (
                  <option value={checkoutForm.tableNumber}>Table {checkoutForm.tableNumber}</option>
                ) : null}
                {tableOptions.map((table) => (
                  <option key={table} value={table}>
                    Table {table}
                  </option>
                ))}
              </select>
            </label>
            {lockedTableNumber ? (
              <small className="qr-table-lock-note">Table {lockedTableNumber} selected from the scanned QR code.</small>
            ) : null}
          </div>
        )}
        <textarea
          value={checkoutForm.notes}
          onChange={(event) => setCheckoutForm({ ...checkoutForm, notes: event.target.value })}
          placeholder="Order notes"
        />
        {checkoutForm.fulfillmentType === "delivery" && !customerSession ? <button className="primary-button full" type="button" onClick={onSelectDelivery}><LogIn size={18} /><span>Sign in to place order</span></button> : <button className="primary-button full" type="submit" disabled={cart.length === 0 || submitting || Boolean(locationError)}>
          <PackageCheck size={18} />
          <span>{submitting ? "Placing..." : "Place order"}</span>
        </button>}
      </form>

    </section>
  );
}

function OrderFeedbackDialog({ order, onSubmit, onClose }: { order: Order; onSubmit: (order: Order, items: ItemFeedbackInput[], message: string) => Promise<void>; onClose: () => void }) {
  const items = order.items.filter((item) => item.status === "active");
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    if (items.some((item) => !ratings[item.id])) { setError("Choose a star rating for every item."); return; }
    setSaving(true); setError("");
    try { await onSubmit(order, items.map((item) => ({ orderItemId: item.id, rating: ratings[item.id], message: comments[item.id] ?? "" })), message); }
    catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to submit feedback."); setSaving(false); }
  }
  return <div className="modal-layer"><button className="modal-backdrop" type="button" onClick={onClose} disabled={saving} aria-label="Close feedback dialog" /><section className="admin-dialog admin-dialog-wide" role="dialog" aria-modal="true" aria-labelledby="order-feedback-title">
    <button className="drawer-close" type="button" onClick={onClose} disabled={saving} aria-label="Close feedback dialog"><X size={20} /></button>
    <div className="admin-dialog-heading"><span><Star size={22} /></span><div><small>{order.orderNumber}</small><h2 id="order-feedback-title">Feedback & Ratings</h2></div></div><p>Rate each item from your completed order.</p>
    <form className="order-feedback-form" onSubmit={submit}><fieldset disabled={saving} className="combo-dialog-fields">
      {items.map((item) => <div className="order-feedback-item" key={item.id}><img src={item.imageUrl || fallbackImages[item.itemType]} alt={item.name} /><div><strong>{item.name}</strong><div className="item-feedback-stars" role="group" aria-label={`Rating for ${item.name}`}>{[1,2,3,4,5].map((rating) => <button key={rating} type="button" aria-label={`Rate ${item.name} ${rating} star${rating === 1 ? "" : "s"}`} aria-pressed={ratings[item.id] === rating} onClick={() => setRatings((current) => ({ ...current, [item.id]: rating }))}><Star size={22} fill={(ratings[item.id] ?? 0) >= rating ? "currentColor" : "none"} /></button>)}</div><textarea aria-label={`Feedback for ${item.name}`} placeholder="Feedback for this item (optional)" value={comments[item.id] ?? ""} onChange={(event) => setComments((current) => ({ ...current, [item.id]: event.target.value }))} /></div></div>)}
      <textarea aria-label="Overall order feedback" placeholder="Overall order feedback (optional)" value={message} onChange={(event) => setMessage(event.target.value)} />
    </fieldset>{error ? <div className="status-banner error" role="alert">{error}</div> : null}<div className="dialog-actions"><button className="secondary-button" type="button" onClick={onClose} disabled={saving}>Cancel</button><button className="primary-button" type="submit" disabled={saving || !items.length}>{saving ? "Sending?" : "Send feedback"}</button></div></form>
  </section></div>;
}

function MyOrderPanel({
  guestView = false,
  customerSession,
  customerOrders,
  onFeedback,
  customerMessage,
  onSignIn,
  onRefresh,
  refreshing,
  currency
}: {
  guestView?: boolean;
  customerSession: CustomerSession | null;
  customerOrders: Order[];
  onFeedback: (order: Order) => void;
  customerMessage: string;
  onSignIn: () => void;
  onRefresh: () => Promise<void>;
  refreshing: boolean;
  currency: string;
}) {
  const currentOrder = customerOrders.find((order) => !["paid", "complete", "returned"].includes(order.status));
  const orderHistory = currentOrder ? customerOrders.filter((order) => order.id !== currentOrder.id) : customerOrders;

  return (
    <section className="surface my-order-panel">
      <div className="section-heading my-order-heading">
        <div className="section-heading-copy">
          <span className="section-title-icon" aria-hidden="true">
            <ClipboardList size={18} strokeWidth={2.2} />
          </span>
          <div>
            <h2>My Order</h2>
            <p>Sign in to track current order status, ready-to-serve time, and your previous cafe orders.</p>
          </div>
        </div>
        {customerSession || guestView ? (
          <button
            className={`icon-button section-refresh-button${refreshing ? " is-loading" : ""}`}
            type="button"
            onClick={() => void onRefresh()}
            disabled={refreshing}
            title="Refresh orders"
            aria-label="Refresh orders"
          >
            <RotateCcw size={18} />
          </button>
        ) : null}
      </div>

      {customerSession && customerMessage ? <StatusBanner tone={customerMessage.toLowerCase().includes("failed") ? "error" : "success"} message={customerMessage} /> : null}

      {!customerSession && !guestView ? (
        <div className="empty-state account-access-prompt">
          <div>
            <strong>Sign in to view My Order</strong>
            <span>Your current orders and private order history are available after signing in.</span>
          </div>
          <button className="primary-button" type="button" onClick={onSignIn}>
            <LogIn size={17} />
            <span>Sign in</span>
          </button>
        </div>
      ) : (
        <div className="customer-orders">
          {currentOrder ? (
            <article className={`order-card ${currentOrder.status} featured-order`}>
              <div className="order-topline">
                <div className="order-identity">
                  <strong>{currentOrder.orderNumber}</strong>
                  <span>{new Date(currentOrder.createdAt).toLocaleString()}</span>
                </div>
                <span className="status-chip">{displayOrderStatus(currentOrder.status, currentOrder.fulfillmentType)}</span>
              </div>
              <p className="order-note">
                Ready to serve {currentOrder.readyAt ? new Date(currentOrder.readyAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "soon"}
              </p>
              {currentOrder.fulfillmentType === "delivery" ? (
                <p className="order-note">Delivery to {currentOrder.deliveryAreaName}: {currentOrder.deliveryAddress}</p>
              ) : currentOrder.tableNumber ? (
                <p className="order-note">Table {currentOrder.tableNumber}</p>
              ) : null}
              <OrderItemsList order={currentOrder} currency={currency} showImages />
              <div className="order-card-summary"><span>Total order price</span><strong>{money(currentOrder.totalCents, currency)}</strong></div>
            </article>
          ) : (
            <div className="empty-state">No active order right now. Paid, completed, and returned orders are listed below.</div>
          )}

          <div className="order-list">
            {orderHistory.map((order) => (
              <article className={`order-card ${order.status}`} key={order.id}>
                <div className="order-topline">
                  <div className="order-identity">
                    <strong>{order.orderNumber}</strong>
                    <span>{new Date(order.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="order-history-status"><span className="status-chip">{displayOrderStatus(order.status, order.fulfillmentType)}</span>{order.status === "complete" && customerSession ? <button className="icon-button order-feedback-button" type="button" title={order.feedbackSubmitted ? "Feedback submitted" : "Give feedback & ratings"} aria-label={order.feedbackSubmitted ? `Feedback submitted for ${order.orderNumber}` : `Give feedback for ${order.orderNumber}`} disabled={order.feedbackSubmitted || !order.items.some((item) => item.status === "active")} onClick={() => onFeedback(order)}><Star size={18} fill={order.feedbackSubmitted ? "currentColor" : "none"} /></button> : null}</div>
                </div>
                <OrderItemsList order={order} currency={currency} showImages />
                <div className="order-actions">
                  <strong>{money(order.totalCents, currency)}</strong>
                  {order.rewardPointsUsed > 0 ? <span>Rewards used: {order.rewardPointsUsed}</span> : null}
                  {order.rewardPointsEarned > 0 ? <span>Rewards earned: {order.rewardPointsEarned}</span> : null}
                  <span>
                    {order.fulfillmentType === "delivery"
                      ? `Delivery: ${order.deliveryAreaName}`
                      : order.tableNumber
                        ? `Table ${order.tableNumber}`
                        : "No table"}
                  </span>
                  <span>Ready: {order.readyAt ? new Date(order.readyAt).toLocaleString() : "Not set"}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function RewardsPanel({
  customerSession,
  rewards,
  currency,
  onRefresh,
  onSignIn
}: {
  customerSession: CustomerSession | null;
  rewards: CustomerRewards | null;
  currency: string;
  onRefresh: () => void;
  onSignIn: () => void;
}) {
  if (!customerSession) {
    return (
      <section className="surface rewards-panel">
        <div className="section-heading">
          <div className="section-heading-copy">
            <span className="section-title-icon" aria-hidden="true">
              <Gift size={18} strokeWidth={2.2} />
            </span>
            <div>
              <h2>My Reward</h2>
              <p>Sign in to view your reward balance and earning or usage history.</p>
            </div>
          </div>
        </div>
        <div className="empty-state account-access-prompt">
          <div>
            <strong>Sign in to view My Reward</strong>
            <span>Your reward balance and transaction history are available after signing in.</span>
          </div>
          <button className="primary-button" type="button" onClick={onSignIn}>
            <LogIn size={17} />
            <span>Sign in</span>
          </button>
        </div>
      </section>
    );
  }

  const balance = rewards?.balance ?? 0;
  const transactions = rewards?.transactions ?? [];

  return (
    <section className="surface rewards-panel">
      <div className="section-heading">
        <div className="section-heading-copy">
          <span className="section-title-icon" aria-hidden="true">
            <Gift size={18} strokeWidth={2.2} />
          </span>
          <div>
            <h2>My Reward</h2>
            <p>Track accumulated reward points, earned points, and used points.</p>
          </div>
        </div>
        <button className="icon-button" type="button" onClick={onRefresh} title="Refresh rewards">
          <RotateCcw size={18} />
        </button>
      </div>

      <div className="reward-hero">
        <Coins size={28} />
        <div>
          <span>Total reward points</span>
          <strong>{balance}</strong>
          <small>Worth {money(balance * 100, currency)} on future orders</small>
        </div>
      </div>

      <div className="reward-history">
        <h3>Reward History</h3>
        {transactions.length === 0 ? (
          <div className="empty-state">No reward activity yet. Points are earned when registered customer orders are marked paid.</div>
        ) : (
          transactions.map((entry) => (
            <article className={`reward-entry ${entry.type}`} key={entry.id}>
              <div className="reward-entry-icon">
                {entry.type === "earned" ? <Coins size={20} /> : <Gift size={20} />}
              </div>
              <div className="reward-entry-content">
                <div className="reward-entry-topline">
                  <div>
                    <strong>{entry.type === "earned" ? "Earned" : "Used"} {Math.abs(entry.pointsDelta)} points</strong>
                    <span>{entry.orderNumber ?? "Order"}</span>
                  </div>
                  <small>{new Date(entry.createdAt).toLocaleString()}</small>
                </div>
                <p>{entry.description}</p>
              </div>
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function OrderItemsList({
  order,
  currency,
  showImages = false,
  locked = false,
  onItemStatus
}: {
  order: Order;
  currency: string;
  showImages?: boolean;
  locked?: boolean;
  onItemStatus?: (itemId: string, status: OrderItemStatus) => void;
}) {
  return (
    <div className="order-item-list">
      {order.items.map((item) => (
        <div className={`order-item-row ${item.status}`} key={item.id}>
          {showImages ? (
            <img
              className="order-item-thumb"
              src={item.imageUrl || (item.itemType === "combo" ? fallbackImages.combo : fallbackImages.menu)}
              alt=""
              loading="lazy"
            />
          ) : null}
          <div>
            <strong>{item.name}</strong>
            <span>Qty {item.quantity} - {money(item.lineTotalCents, currency)}</span>
          </div>
          {onItemStatus ? (
            locked ? (
              <span className="item-status-label">{orderItemStatusLabels[item.status]}</span>
            ) : (
              <select
                className="item-status-select"
                value={item.status}
                onChange={(event) => onItemStatus(item.id, event.target.value as OrderItemStatus)}
                aria-label={`Status for ${item.name}`}
              >
                {(Object.keys(orderItemStatusLabels) as OrderItemStatus[]).map((status) => (
                  <option key={status} value={status}>{orderItemStatusLabels[status]}</option>
                ))}
              </select>
            )
          ) : (
            <span>{orderItemStatusLabels[item.status]}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function FieldIcon({ icon: Icon, children }: { icon: typeof User; children: React.ReactNode }) {
  return (
    <label className="field-icon">
      <Icon size={17} />
      {children}
    </label>
  );
}

function Stepper({ value, onMinus, onPlus }: { value: number; onMinus: () => void; onPlus: () => void }) {
  return (
    <div className="stepper">
      <button type="button" onClick={onMinus} aria-label="Decrease quantity">
        <X size={13} />
      </button>
      <strong>{value}</strong>
      <button type="button" onClick={onPlus} aria-label="Increase quantity">
        <Plus size={13} />
      </button>
    </div>
  );
}

function DeliveryAreaStatusBoard({
  deliveryAreas,
  orders,
  currency,
  dateRange,
  setDateRange,
  onRefresh,
  onOpenOrder
}: {
  deliveryAreas: DeliveryArea[];
  orders: Order[];
  currency: string;
  dateRange: { from: string; to: string };
  setDateRange: (value: { from: string; to: string }) => void;
  onRefresh: () => void;
  onOpenOrder: (order: Order) => void;
}) {
  const activeDeliveryStatuses = new Set<OrderStatus>(["received", "processing", "served", "paid"]);
  const invalidRange = Boolean(dateRange.from && dateRange.to && dateRange.from > dateRange.to);
  const rangedOrders = orders.filter(order => { const date = cafeDate(order.createdAt); return !invalidRange && (!dateRange.from || date >= dateRange.from) && (!dateRange.to || date <= dateRange.to); });
  const activeDeliveryCount = rangedOrders.filter((order) => activeDeliveryStatuses.has(order.status)).length;

  return (
    <section className="delivery-area-status-section">
      <div className="section-heading compact">
        <div>
          <h2>Delivery Area Status</h2>
          <div className="status-board-description"><p>Each area shows delivery volume and the latest delivery status.</p><button className="icon-button" type="button" onClick={onRefresh} title="Refresh delivery areas" aria-label="Refresh delivery areas"><RotateCcw size={18} /></button></div>
        </div>
        <div className="metric-strip">
          <MiniMetric label="Areas" value={String(deliveryAreas.filter((area) => area.isActive).length)} />
          <MiniMetric label="Active delivery" value={String(activeDeliveryCount)} />
        </div>
      </div>

      <div className="delivery-status-date-range">
        <label>From<input type="date" aria-label="Delivery status date from" value={dateRange.from} max={dateRange.to || undefined} onChange={event => setDateRange({ ...dateRange, from: event.target.value })} /></label>
        <label>To<input type="date" aria-label="Delivery status date to" value={dateRange.to} min={dateRange.from || undefined} onChange={event => setDateRange({ ...dateRange, to: event.target.value })} /></label>
        <button className="secondary-button" type="button" onClick={() => setDateRange({ from: cafeDate(), to: cafeDate() })}><CalendarDays size={17} />Today</button>
      </div>
      {invalidRange ? <div className="status-banner error" role="alert">Choose an end date on or after the start date.</div> : null}
      <div className="delivery-area-status-grid">
        {deliveryAreas.length === 0 ? (
          <div className="empty-state">No delivery areas configured. Add them from Settings.</div>
        ) : (
          deliveryAreas.map((area) => {
            const areaOrders = rangedOrders.filter((order) => order.deliveryAreaId === area.id).sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
            const latestOrder = areaOrders[0] ?? null;
            const activeOrders = areaOrders.filter((order) => activeDeliveryStatuses.has(order.status));
            const statusCounts = orderStatuses
              .map((status) => ({ status, count: areaOrders.filter((order) => order.status === status).length }))
              .filter(({ count }) => count > 0);

            return (
              <button
                className={`delivery-area-status-card ${activeOrders.length ? "active-deliveries" : "idle"}${area.isActive ? "" : " inactive"}`}
                key={area.id}
                type="button"
                disabled={!latestOrder}
                onClick={() => latestOrder && onOpenOrder(latestOrder)}
              >
                <div className="delivery-area-card-heading">
                  <span className="table-number">
                    <MapPin size={18} />
                    {area.name}
                  </span>
                  <strong>{money(area.chargeCents, currency)}</strong>
                </div>
                <div className="delivery-area-volume">
                  <strong>{areaOrders.length}</strong>
                  <span>{areaOrders.length === 1 ? "delivery" : "deliveries"}</span>
                </div>
                {latestOrder ? (
                  <>
                    <div className="delivery-latest-order">
                      <span>Latest</span>
                      <strong>{latestOrder.orderNumber}</strong>
                    </div>
                    <span className="status-chip">{displayOrderStatus(latestOrder.status, "delivery")}</span>
                    <div className="delivery-status-counts">
                      {statusCounts.map(({ status, count }) => (
                        <small key={status}>{displayOrderStatus(status, "delivery")}: {count}</small>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <strong>No deliveries in this date range</strong>
                    <span>{area.isActive ? "Ready for online orders" : "Area inactive"}</span>
                  </>
                )}
              </button>
            );
          })
        )}
      </div>
    </section>
  );
}

function TableOccupancyBoard({
  tables,
  onRefresh,
  onOpenOrder
}: {
  tables: TableOccupancy[];
  onRefresh: () => void;
  onOpenOrder: (order: NonNullable<TableOccupancy["order"]>) => void;
}) {
  const occupiedCount = tables.filter((table) => table.isOccupied).length;

  return (
    <section className="table-status-section">
      <div className="section-heading compact">
        <div>
          <h2>Table Status</h2>
          <div className="status-board-description"><p>Occupied tables show active order number, customer name, and order status.</p><button className="icon-button" type="button" onClick={onRefresh} title="Refresh tables" aria-label="Refresh tables"><RotateCcw size={18} /></button></div>
        </div>
        <div className="metric-strip">
          <MiniMetric label="Occupied" value={String(occupiedCount)} />
          <MiniMetric label="Vacant" value={String(Math.max(0, tables.length - occupiedCount))} />
        </div>
      </div>

      <div className="table-grid">
        {tables.map((table) => (
          <button
            className={table.isOccupied ? "table-card occupied" : "table-card free"}
            key={table.tableNumber}
            type="button"
            aria-label={`Table ${table.tableNumber}, ${table.isOccupied ? "occupied" : "vacant"}${table.order ? `. View order ${table.order.orderNumber}` : ""}`}
            disabled={!table.order}
            onClick={() => table.order && onOpenOrder(table.order)}
          >
            <span className="table-card-header"><span className="table-card-icon"><Armchair size={20} aria-hidden="true" /></span><span className="table-occupancy-badge"><span className="table-state-dot" />{table.isOccupied ? "Occupied" : "Vacant"}</span></span>
            <span className="table-card-identity"><small>Café table</small><strong>Table {table.tableNumber}</strong></span>
            {table.order ? (
              <>
                <span className="table-card-order"><span className="table-order-number">{table.order.orderNumber}</span><span className="table-customer-name"><User size={14} aria-hidden="true" />{table.order.customerName || "Guest"}</span></span>
                <span className="table-card-footer"><span className="table-workflow-chip">{statusLabels[table.order.status]}</span><span className="table-view-order">View order<ArrowUpRight size={16} aria-hidden="true" /></span></span>
              </>
            ) : (
              <>
                <span className="table-card-order table-availability-note">Ready for your next guest</span>
                <span className="table-card-footer"><span className="table-available-label"><CircleCheck size={16} aria-hidden="true" />Available to seat</span></span>
              </>
            )}
          </button>
        ))}
      </div>
    </section>
  );
}

function OrderBoard({
  orders,
  allOrders,
  currency,
  activeStatus,
  onFilter,
  onResetFilter,
  historyRange,
  setHistoryRange,
  onDownload,
  onRefresh,
  onConfirm,
  rewardRate,
  focusedOrderId,
  onFocusConsumed,
  deliveryOnly = false
}: {
  orders: Order[];
  allOrders: Order[];
  currency: string;
  activeStatus: OrderStatus | "all";
  onFilter: (status: OrderStatus) => void;
  onResetFilter: () => void;
  historyRange: { from: string; to: string };
  setHistoryRange: (value: { from: string; to: string }) => void;
  onDownload: (format: "csv" | "pdf") => void;
  onRefresh: () => void;
  onConfirm: (original: Order, draft: Order) => Promise<void>;
  rewardRate: number;
  focusedOrderId: string | null;
  onFocusConsumed?: () => void;
  deliveryOnly?: boolean;
}) {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  useEffect(() => {
    if (focusedOrderId) {
      setSelectedOrder(allOrders.find((order) => order.id === focusedOrderId) ?? null);
      onFocusConsumed?.();
    }
  }, [focusedOrderId, allOrders, onFocusConsumed]);
  const invalidDateRange = Boolean(historyRange.from && historyRange.to && historyRange.from > historyRange.to);
  const matchesDateRange = (order: Order) => {
    const date = order.createdAt.slice(0, 10);
    return !invalidDateRange && (!historyRange.from || date >= historyRange.from) && (!historyRange.to || date <= historyRange.to);
  };
  const visibleOrders = orders.filter(matchesDateRange);
  const counts = orderStatuses.map(status => {
    const matching = allOrders.filter(order => matchesDateRange(order) && order.status === status);
    return { status, count: matching.length, totalCents: matching.reduce((sum, order) => sum + order.totalCents, 0) };
  });

  return (
    <div className="orders-area">
      <div className="metric-strip admin-metrics">
        {counts.map(({ status, count, totalCents }) => {
          const Icon = status === "served" && deliveryOnly ? Truck : orderStatusIcons[status];
          return (
          <button
            className={`status-filter-card status-${status}${activeStatus === status ? " active" : ""}`}
            key={status}
            type="button"
            aria-pressed={activeStatus === status}
            onClick={() => onFilter(status)}
          >
            <div className="status-card-topline"><span className="status-card-icon"><Icon size={20} aria-hidden="true" /></span><strong>{count}</strong></div>
            <span className="status-card-label">{displayOrderStatus(status, deliveryOnly ? "delivery" : "cafe")}</span>
            <div className="status-card-value"><small>Total order value</small><b>{money(totalCents, currency)}</b></div>
          </button>
          );
        })}
        <button className="status-filter-card reset" type="button" onClick={onResetFilter}>
          <RotateCcw size={17} />
          <span>Reset</span>
        </button>
      </div>

      <div className="history-tools">
        <div>
          <strong>{deliveryOnly ? "Online Orders" : "Cafe Orders"}</strong>
          <span>
            {activeStatus === "all"
              ? `Showing ${deliveryOnly ? "online orders" : "cafe orders"} in the selected date range.`
              : `Filtered by ${displayOrderStatus(activeStatus, deliveryOnly ? "delivery" : "cafe")} and the selected date range.`}
          </span>
        </div>
        <label>
          <CalendarDays size={16} />
          <input type="date" aria-label="Order date from" max={historyRange.to || undefined} value={historyRange.from} onChange={(event) => setHistoryRange({ ...historyRange, from: event.target.value })} />
        </label>
        <label>
          <CalendarDays size={16} />
          <input type="date" aria-label="Order date to" min={historyRange.from || undefined} value={historyRange.to} onChange={(event) => setHistoryRange({ ...historyRange, to: event.target.value })} />
        </label>
        <button type="button" disabled={invalidDateRange} onClick={() => onDownload("csv")}>
          <Download size={16} />
          Excel
        </button>
        <button type="button" disabled={invalidDateRange} onClick={() => onDownload("pdf")}>
          <FileText size={16} />
          PDF
        </button>
        <button type="button" onClick={onRefresh} title="Refresh orders" aria-label="Refresh orders">
          <RotateCcw size={17} />
        </button>
      </div>
      {invalidDateRange ? <div className="status-banner error" role="alert">The start date must be on or before the end date.</div> : null}
      <div className="admin-orders-table-wrap">
        <table className="admin-orders-table">
          <caption>{deliveryOnly ? "Online orders" : "Cafe orders"} · {visibleOrders.length} orders</caption>
          <thead><tr>
            <th scope="col">Order</th><th scope="col">{deliveryOnly ? "Customer name" : "Guest name"}</th>
            {!deliveryOnly ? <th scope="col">Table no.</th> : null}
            <th scope="col">Items ordered</th><th scope="col">Total order price</th><th scope="col">Order status</th>
          </tr></thead>
          <tbody>
            {visibleOrders.map((order) => (
              <tr key={order.id} id={`order-${order.id}`} className={`order-table-row ${order.status}`}
                onClick={() => setSelectedOrder(order)}>
                <td><button type="button" className="order-details-link" onClick={(event) => { event.stopPropagation(); setSelectedOrder(order); }} aria-label={`View order ${order.orderNumber}`}>{order.orderNumber}</button><small>{new Date(order.createdAt).toLocaleString()}</small></td>
                <td className="order-customer-cell"><strong>{order.customerName.trim() || "Guest"}</strong><span><Phone size={13} aria-hidden="true" />{order.mobile || "No mobile"}</span><span><Mail size={13} aria-hidden="true" />{order.email || "Guest"}</span></td>
                {!deliveryOnly ? <td>{order.tableNumber || "—"}</td> : null}
                <td><ul className="order-table-items">{order.items.map((item) => <li key={item.id}>{item.name}</li>)}</ul></td>
                <td className="order-price-cell">{money(order.totalCents, currency)}</td>
                <td><span className={`status-chip order-status-${order.status}`}>{displayOrderStatus(order.status, order.fulfillmentType)}</span></td>
              </tr>
            ))}
            {!visibleOrders.length ? <tr><td colSpan={deliveryOnly ? 5 : 6}><div className="empty-state">No {deliveryOnly ? "online" : "cafe"} orders match the selected status and date range.</div></td></tr> : null}
          </tbody>
        </table>
      </div>
      {selectedOrder ? <OrderDetailsDialog key={selectedOrder.id} order={selectedOrder} currency={currency} rewardRate={rewardRate} onClose={() => setSelectedOrder(null)} onConfirm={onConfirm} /> : null}
    </div>
  );
}

function OrderDetailsDialog({ order, currency, rewardRate, onClose, onConfirm }: {
  order: Order; currency: string; rewardRate: number; onClose: () => void;
  onConfirm: (original: Order, draft: Order) => Promise<void>;
}) {
  const [draft, setDraft] = useState<Order>(() => ({ ...order, items: order.items.map((item) => ({ ...item })) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const locked = order.status === "complete";
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const dialog = document.getElementById("order-details-dialog");
    dialog?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
      if (event.key !== "Tab") return;
      const controls = Array.from(dialog?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]') ?? []);
      const first = controls[0]; const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", handleKey);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", handleKey); previousFocus?.focus(); };
  }, [onClose, saving]);
  const subtotal = draft.items.filter((item) => item.status === "active").reduce((sum, item) => sum + item.lineTotalCents, 0);
  const preview = { ...draft, totalCents: draft.status === "returned" ? 0 : Math.max(0, subtotal - Math.min(draft.rewardDiscountCents, subtotal) - draft.additionalDiscountCents + draft.deliveryChargeCents) };
  async function confirm() {
    if (saving) return;
    setSaving(true); setError("");
    try { await onConfirm(order, draft); onClose(); }
    catch (failure) { setError((failure instanceof Error ? failure.message : "Unable to save order.") + " Review and retry to finish saving your changes."); setSaving(false); }
  }
  return (
    <div className="modal-layer">
      <button className="modal-backdrop" type="button" onClick={onClose} disabled={saving} aria-label="Close order details" />
      <section className="admin-dialog admin-dialog-wide order-details-dialog" id="order-details-dialog" role="dialog" aria-modal="true" aria-labelledby="order-details-title" tabIndex={-1}>
        <button className="drawer-close" type="button" onClick={onClose} disabled={saving} aria-label="Close order details"><X size={20} /></button>
        <div className="admin-dialog-heading"><span><ClipboardList size={24} /></span><div><small>Order details · {order.fulfillmentType === "delivery" ? "Online" : "Cafe"}</small><h2 id="order-details-title">{order.orderNumber}</h2></div></div>
        <div className="customer-line"><span>{order.customerName || "Guest"}</span><span>{order.mobile || "No mobile"}</span><span>{order.email || "Guest"}</span><span>{order.fulfillmentType === "delivery" ? `${order.deliveryAreaName} · ${order.deliveryAddress}` : `Table ${order.tableNumber || "—"}`}</span><span>{new Date(order.createdAt).toLocaleString()}</span></div>
        {order.notes ? <p className="order-note">{order.notes}</p> : null}
        <fieldset disabled={saving || locked} className="order-details-fields">
          <OrderItemsList order={draft} currency={currency} locked={locked} onItemStatus={(itemId, status) => setDraft((current) => ({ ...current, items: current.items.map((item) => item.id === itemId ? { ...item, status } : item) }))} />
          <AdminPaymentSummary order={preview} currency={currency} rewardRate={rewardRate} locked={locked}
            onStatus={(_, status) => setDraft((current) => ({ ...current, status }))}
            onAdditionalDiscount={(_, additionalDiscountCents) => setDraft((current) => ({ ...current, additionalDiscountCents }))} />
          {draft.status === "processing" ? <label className="ready-control"><span>Ready-to-serve time</span><input type="datetime-local" value={toDateTimeLocalValue(draft.readyAt)} onChange={(event) => { const value = event.target.value; setDraft((current) => ({ ...current, readyAt: value ? new Date(value).toISOString() : null })); }} /></label> : null}
        </fieldset>
        {error ? <div className="status-banner error" role="alert">{error}</div> : null}
        <div className="order-dialog-footer"><span>{locked ? "Completed orders are locked." : "Changes are saved when you click Confirm."}</span><div><button className="secondary-button" type="button" onClick={onClose} disabled={saving}>{locked ? "Close" : "Cancel"}</button>{!locked ? <button className="primary-button" type="button" onClick={() => void confirm()} disabled={saving}>{saving ? "Saving…" : "Confirm"}</button> : null}</div></div>
      </section>
    </div>
  );
}

function AdminPaymentSummary({
  order,
  currency,
  rewardRate,
  onStatus,
  onAdditionalDiscount,
  locked = order.status === "complete"
}: {
  order: Order;
  currency: string;
  rewardRate: number;
  onStatus: (orderId: string, status: OrderStatus) => void;
  onAdditionalDiscount: (order: Order, additionalDiscountCents: number) => void;
  locked?: boolean;
}) {
  const activeSubtotalCents = order.items
    .filter((item) => item.status === "active")
    .reduce((sum, item) => sum + item.lineTotalCents, 0);
  const estimatedRewards = Math.max(0, Math.floor((order.totalCents / 100) * (Number.isFinite(rewardRate) ? rewardRate : 0)));
  const grandTotalRewards = order.rewardPointsEarned || estimatedRewards;

  return (
    <div className="admin-payment-summary">
      <div>
        <span>Eligible item total</span>
        <strong>{money(activeSubtotalCents, currency)}</strong>
      </div>
      <div>
        <span>Grand total rewards</span>
        <strong>{grandTotalRewards} points</strong>
        {order.rewardPointsUsed > 0 ? (
          <small>{order.rewardPointsUsed} points used ({money(Math.min(order.rewardDiscountCents, activeSubtotalCents), currency)} discount)</small>
        ) : null}
      </div>
      <label>
        <span>Additional discount ({currency})</span>
        <input
          type="number"
          min="0"
          step="0.01"
          defaultValue={(order.additionalDiscountCents / 100).toFixed(2)}
          disabled={locked}
          onBlur={(event) => onAdditionalDiscount(order, Math.max(0, Math.round(Number(event.currentTarget.value) * 100) || 0))}
          aria-label={`Additional discount for ${order.orderNumber}`}
        />
      </label>
      <div className="grand-total-row">
        <span>Grand total amount</span>
        <strong>{money(order.totalCents, currency)}</strong>
        {order.deliveryChargeCents > 0 ? <small>Includes {money(order.deliveryChargeCents, currency)} delivery</small> : null}
      </div>
      <label>
        <span>Status - all items {order.tableNumber ? `at Table ${order.tableNumber}` : "in this order"}</span>
        {locked ? (
          <strong className="locked-chip">Complete - locked</strong>
        ) : (
          <select value={order.status} onChange={(event) => onStatus(order.id, event.target.value as OrderStatus)}>
            {orderStatuses.map((status) => (
              <option key={status} value={status}>{displayOrderStatus(status, order.fulfillmentType)}</option>
            ))}
          </select>
        )}
      </label>
    </div>
  );
}

function DeliveryAreaEditor({ mapsApiKey, form, setForm, deliveryAreas, currency, onSubmit }: {
  mapsApiKey: string;
  form: typeof emptyDeliveryAreaForm;
  setForm: (value: typeof emptyDeliveryAreaForm) => void;
  deliveryAreas: DeliveryArea[];
  currency: string;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<boolean>;
}) {
  const [expanded, setExpanded] = useState(true);
  const contentId = useId();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const visible = deliveryAreas.filter(area => [area.name, area.centerAddress, String(area.radiusMiles), area.isActive ? "available" : "not available"].join(" ").toLowerCase().includes(search.trim().toLowerCase()));
  function edit(area: DeliveryArea) {
    setForm({ id: area.id, name: area.name, charge: (area.chargeCents / 100).toFixed(2), sortOrder: area.sortOrder, isActive: area.isActive, centerLatitude: area.centerLatitude, centerLongitude: area.centerLongitude, centerAddress: area.centerAddress, radiusMiles: area.radiusMiles });
    setError(""); setOpen(true);
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    setSaving(true); setError("");
    try { if (await onSubmit(event)) setOpen(false); else setError("Could not save the delivery area. Check its details and try again."); }
    catch (failure) { setError(failure instanceof Error ? failure.message : "Could not save delivery area."); }
    finally { setSaving(false); }
  }
  return <section className="admin-panel delivery-area-editor">
    <div className="settings-section-heading settings-delivery-heading"><SettingsSectionHeading expanded={expanded} contentId={contentId} label="Delivery areas & charges" disabled={saving || open} onToggle={() => setExpanded(value => !value)}><div className="section-heading compact-heading"><div><h2>Delivery areas & charges</h2><p>Manage delivery coverage and charges.</p></div></div></SettingsSectionHeading>{expanded ? <button className="primary-button" type="button" onClick={() => { setForm(emptyDeliveryAreaForm); setError(""); setOpen(true); }}><Plus size={17} />Add delivery area</button> : null}</div>
    <div id={contentId} className="settings-delivery-content" hidden={!expanded}>
    <label className="search-box delivery-area-search"><Search size={18} /><input aria-label="Search delivery areas" placeholder="Search delivery areas or addresses" value={search} onChange={event => setSearch(event.target.value)} /></label>
    <div className="admin-orders-table-wrap"><table className="admin-orders-table delivery-areas-table"><caption>{visible.length} delivery area{visible.length === 1 ? "" : "s"}</caption><thead><tr><th>Delivery area</th><th>Center address</th><th>Radius</th><th>Delivery charge</th><th>Status</th></tr></thead><tbody>{visible.map(area => <tr key={area.id} className="order-table-row" tabIndex={0} aria-label={`Edit ${area.name}`} onClick={() => edit(area)} onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); edit(area); } }}><td><strong>{area.name}</strong></td><td>{area.centerAddress || (area.centerLatitude == null ? "Center pin required" : `${area.centerLatitude}, ${area.centerLongitude}`)}</td><td>{area.radiusMiles} mile{area.radiusMiles === 1 ? "" : "s"}</td><td className="order-price-cell">{money(area.chargeCents, currency)}</td><td><span className={`catalog-status ${area.isActive ? "active" : "inactive"}`}>{area.isActive ? "Available" : "Not available"}</span></td></tr>)}</tbody></table>{!visible.length ? <div className="empty-state">{deliveryAreas.length ? "No matching delivery areas." : "No delivery areas yet. Add your first area."}</div> : null}</div>
    </div>
    {open ? <div className="modal-layer"><button className="modal-backdrop" type="button" disabled={saving} aria-label="Close delivery area dialog" onClick={() => setOpen(false)} /><section className="admin-dialog admin-dialog-wide delivery-area-dialog" role="dialog" aria-modal="true" aria-labelledby="delivery-area-dialog-title"><button className="drawer-close" type="button" disabled={saving} onClick={() => setOpen(false)} aria-label="Close delivery area dialog"><X size={20} /></button><div className="admin-dialog-heading"><span><MapPin size={22} /></span><div><small>Delivery coverage</small><h2 id="delivery-area-dialog-title">{form.id ? "Edit delivery area" : "Add delivery area"}</h2></div></div>
        <form className="admin-form" onSubmit={submit}><fieldset className="combo-dialog-fields" disabled={saving}>
          <label>Delivery area name<input aria-label="Delivery area name"
            required
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            placeholder="Delivery area name"
          /></label>
          <label>Delivery charge<input aria-label="Delivery charge"
            required
            min="0"
            step="0.01"
            type="number"
            value={form.charge}
            onChange={(event) => setForm({ ...form, charge: event.target.value })}
            placeholder={`Delivery charge (${currency})`}
          /></label>
          <label>Sort order<input
            type="number"
            value={form.sortOrder}
            onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value) })}
            placeholder="Sort order"
          /></label>
          <DeliveryMap apiKey={mapsApiKey} label="Delivery area center" point={form.centerLatitude != null && form.centerLongitude != null ? { latitude: form.centerLatitude, longitude: form.centerLongitude, address: form.centerAddress } : null} boundary={form} onChange={point => setForm({ ...form, centerLatitude: point.latitude, centerLongitude: point.longitude, centerAddress: point.address })} />
          <label>Allowed radius (miles)<input required type="number" min="0.01" step="0.01" value={form.radiusMiles} onChange={event => setForm({ ...form, radiusMiles: Number(event.target.value) })} /></label>
          {form.centerLatitude == null ? <small>Pin the center before saving this area.</small> : null}
          <label className="check-line">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
            />
            Available to customers
          </label>
          </fieldset>
          {error ? <div className="status-banner error" role="alert">{error}</div> : null}
          <div className="dialog-actions"><button className="secondary-button" type="button" disabled={saving} onClick={() => setOpen(false)}>Cancel</button>
          <button className="primary-button" type="submit" disabled={saving || form.centerLatitude == null || form.centerLongitude == null || form.radiusMiles <= 0}>
            <Save size={17} />
            <span>{saving ? "Saving..." : form.id ? "Save changes" : "Add delivery area"}</span>
          </button></div>
        </form>

    </section></div> : null}
  </section>;
}

function CatalogSection({ title, children }: { title: string; children: ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  return <section className="admin-panel catalog-collapsible">
    <h3 className="catalog-collapse-heading"><button className="catalog-collapse-toggle" type="button" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}><span>{title}</span><ChevronDown size={20} aria-hidden="true" /></button></h3>
    {expanded ? <div className="catalog-section-content">{children}</div> : null}
  </section>;
}

function CategoryEditor({
  categories,
  onAdd,
  onEdit,
  onDeactivate
}: {
  categories: Category[];
  onAdd: () => void;
  onEdit: (category: Category) => void;
  onDeactivate: (id: string) => void;
}) {
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const filteredCategories = categories.filter((category) => `${category.name} ${category.description}`.toLowerCase().includes(query));
  return (
    <CatalogSection title="Categories">
      <div className="section-heading compact-heading catalog-panel-heading">
        <div>
          <p>Organize menu items into customer-friendly groups.</p>
        </div>
        <button className="primary-button small" type="button" onClick={onAdd}>
          <Plus size={16} />
          <span>Add category</span>
        </button>
      </div>
      <label className="search-box admin-catalog-search">
        <Search size={17} aria-hidden="true" />
        <input type="search" aria-label="Search categories" placeholder="Search categories..." value={search} onChange={(event) => setSearch(event.target.value)} />
      </label>
      <div className="admin-list">
        {filteredCategories.map((category) => (
          <div className="admin-row" key={category.id}>
            <div>
              <strong>{category.name}</strong>
              <span className={`catalog-status ${category.isActive ? "active" : "inactive"}`}>{category.isActive ? "Active" : "Inactive"}</span>
            </div>
            <button type="button" onClick={() => onEdit(category)}>
              Edit
            </button>
            <button type="button" onClick={() => onDeactivate(category.id)} title="Deactivate category">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        {!filteredCategories.length ? <div className="empty-state">{query ? "No categories match your search." : "No categories yet."}</div> : null}
      </div>
    </CatalogSection>
  );
}

function MenuEditor({
  menuItems,
  currency,
  onAdd,
  onEdit,
  onDeactivate
}: {
  menuItems: MenuItem[];
  currency: string;
  onAdd: () => void;
  onEdit: (item: MenuItem) => void;
  onDeactivate: (id: string) => void;
}) {
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const filteredItems = menuItems.filter((item) => `${item.name} ${item.categoryName} ${item.description}`.toLowerCase().includes(query));
  return (
    <CatalogSection title="Menu Items">
      <div className="section-heading compact-heading catalog-panel-heading">
        <div>
          <p>Review the menu, pricing, nutrition, and availability.</p>
        </div>
        <button className="primary-button small" type="button" onClick={onAdd}>
          <Plus size={16} />
          <span>Add menu item</span>
        </button>
      </div>
      <label className="search-box admin-catalog-search">
        <Search size={17} aria-hidden="true" />
        <input type="search" aria-label="Search menu items" placeholder="Search menu items or categories..." value={search} onChange={(event) => setSearch(event.target.value)} />
      </label>
      <div className="admin-list compact">
        {filteredItems.map((item) => (
          <div className="admin-row" key={item.id}>
            <img src={item.imageUrl || fallbackImages.menu} alt="" />
            <div>
              <strong>{item.name}</strong>
              <span>
                {item.categoryName} - {money(item.priceCents, currency)}
              </span>
              <span className={`catalog-status ${item.isAvailable ? "active" : "inactive"}`}>{item.isAvailable ? "Active" : "Inactive"}</span>
            </div>
            <button type="button" onClick={() => onEdit(item)}>
              Edit
            </button>
            <button type="button" onClick={() => onDeactivate(item.id)} title="Deactivate item">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        {!filteredItems.length ? <div className="empty-state">{query ? "No menu items match your search." : "No menu items yet."}</div> : null}
      </div>
    </CatalogSection>
  );
}

function ImageUploadField({
  className = "",
  inputName,
  label = "Image URL",
  uploadLabel = "Upload image to R2",
  previewAlt = "Selected menu item preview",
  showPreview = true,
  value,
  onChange,
  onUpload
}: {
  className?: string;
  inputName?: string;
  label?: string;
  uploadLabel?: string;
  previewAlt?: string;
  showPreview?: boolean;
  value: string;
  onChange: (imageUrl: string) => void;
  onUpload: (file: File) => Promise<string>;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function handleImage(file: File | undefined) {
    if (!file) return;
    if (!(["image/jpeg", "image/png", "image/webp", "image/avif", "image/gif"] as string[]).includes(file.type)) {
      setError("Choose a JPG, PNG, WebP, AVIF, or GIF image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image files must be 5 MB or smaller.");
      return;
    }

    setUploading(true);
    setError("");
    try {
      onChange(await onUpload(file));
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "Image upload failed.");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className={`image-upload-field${className ? ` ${className}` : ""}`}>
      <label>
        <span>{label}</span>
        <input
          name={inputName}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Upload or enter an image URL"
        />
      </label>
      <div className="image-upload-row">
        <label className={`secondary-button image-upload-button${uploading ? " is-uploading" : ""}`}>
          <ImagePlus size={17} />
          <span>{uploading ? "Uploading..." : uploadLabel}</span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif,image/gif"
            disabled={uploading}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              void handleImage(file);
            }}
          />
        </label>
        {value && showPreview ? <img className="image-upload-preview" src={value} alt={previewAlt} /> : null}
      </div>
      {error ? <span className="image-upload-error" role="alert">{error}</span> : null}
    </div>
  );
}

function CategoryDialog({
  form,
  setForm,
  onSubmit,
  onClose
}: {
  form: typeof emptyCategoryForm;
  setForm: (value: typeof emptyCategoryForm) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-layer">
      <button className="modal-backdrop" type="button" onClick={onClose} aria-label="Close category dialog" />
      <section className="admin-dialog admin-dialog-compact" role="dialog" aria-modal="true" aria-labelledby="category-dialog-title">
        <button className="drawer-close" type="button" onClick={onClose} aria-label="Close category dialog">
          <X size={20} />
        </button>
        <div className="admin-dialog-heading">
          <span><ClipboardList size={20} /></span>
          <div>
            <small>Catalog</small>
            <h2 id="category-dialog-title">{form.id ? "Edit category" : "Add category"}</h2>
          </div>
        </div>
        <form className="admin-form dialog-form" onSubmit={onSubmit}>
          <label>
            <span>Category name</span>
            <input required autoFocus value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </label>
          <label>
            <span>Description</span>
            <textarea
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              placeholder="Short customer-facing description"
            />
          </label>
          <label>
            <span>Sort order</span>
            <input
              type="number"
              value={form.sortOrder}
              onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value) })}
            />
          </label>
          <label className="check-line">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
            />
            Active
          </label>
          <div className="dialog-actions">
            <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
            <button className="primary-button" type="submit">
              <Save size={17} />
              <span>{form.id ? "Save category" : "Add category"}</span>
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function MenuItemDialog({
  form,
  setForm,
  categories,
  onImageUpload,
  onSubmit,
  onClose
}: {
  form: typeof emptyMenuForm;
  setForm: React.Dispatch<React.SetStateAction<typeof emptyMenuForm>>;
  categories: Category[];
  onImageUpload: (file: File) => Promise<string>;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
}) {
  return (
    <div className="modal-layer">
      <button className="modal-backdrop" type="button" onClick={onClose} aria-label="Close menu item dialog" />
      <section className="admin-dialog admin-dialog-wide" role="dialog" aria-modal="true" aria-labelledby="menu-dialog-title">
        <button className="drawer-close" type="button" onClick={onClose} aria-label="Close menu item dialog">
          <X size={20} />
        </button>
        <div className="admin-dialog-heading">
          <span><Utensils size={20} /></span>
          <div>
            <small>Catalog</small>
            <h2 id="menu-dialog-title">{form.id ? "Edit menu item" : "Add menu item"}</h2>
          </div>
        </div>
        <form className="admin-form two-col dialog-form" onSubmit={onSubmit}>
          <label>
            <span>Category</span>
            <select required value={form.categoryId} onChange={(event) => setForm({ ...form, categoryId: event.target.value })}>
              <option value="">Select category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Item name</span>
            <input required autoFocus value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </label>
          <label>
            <span>Price</span>
            <input
              required
              type="number"
              min="0"
              step="0.01"
              value={form.price}
              onChange={(event) => setForm({ ...form, price: event.target.value })}
            />
          </label>
          <ImageUploadField
            value={form.imageUrl}
            onChange={(imageUrl) => setForm((current) => ({ ...current, imageUrl }))}
            onUpload={onImageUpload}
          />
          <label className="menu-detail-field">
            <span>Description</span>
            <textarea
              className="menu-detail-textarea"
              rows={7}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </label>
          <label className="menu-detail-field">
            <span>Ingredients</span>
            <textarea
              className="menu-detail-textarea"
              rows={7}
              value={form.ingredientsText}
              onChange={(event) => setForm({ ...form, ingredientsText: event.target.value })}
              placeholder="Separate ingredients with commas"
            />
          </label>
          {(["calories", "carbs", "protein", "fat", "fiber"] as const).map((key) => (
            <label key={key}>
              <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
              <input
                type="number"
                min="0"
                value={form[key]}
                onChange={(event) => setForm({ ...form, [key]: event.target.value })}
              />
            </label>
          ))}
          <label>
            <span>Rating</span>
            <input
              type="number"
              min="0"
              max="5"
              step="0.1"
              value={form.rating}
              onChange={(event) => setForm({ ...form, rating: event.target.value })}
            />
          </label>
          <label>
            <span>Rating count</span>
            <input
              type="number"
              min="0"
              value={form.ratingCount}
              onChange={(event) => setForm({ ...form, ratingCount: event.target.value })}
            />
          </label>
          <label>
            <span>Sort order</span>
            <input
              type="number"
              value={form.sortOrder}
              onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value) })}
            />
          </label>
          <label className="check-line">
            <input
              type="checkbox"
              checked={form.isBestSeller}
              onChange={(event) => setForm({ ...form, isBestSeller: event.target.checked })}
            />
            Best seller
          </label>
          <label className="check-line">
            <input
              type="checkbox"
              checked={form.isAvailable}
              onChange={(event) => setForm({ ...form, isAvailable: event.target.checked })}
            />
            Available
          </label>
          <div className="dialog-actions">
            <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
            <button className="primary-button" type="submit">
              <Save size={17} />
              <span>{form.id ? "Save item" : "Add item"}</span>
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function ComboEditor({
  form,
  setForm,
  selections,
  setSelections,
  menuItems,
  combos,
  currency,
  onImageUpload,
  onSubmit,
  onDeactivate
}: {
  form: typeof emptyComboForm;
  setForm: React.Dispatch<React.SetStateAction<typeof emptyComboForm>>;
  selections: Record<string, number>;
  setSelections: (value: Record<string, number>) => void;
  menuItems: MenuItem[];
  combos: Combo[];
  currency: string;
  onImageUpload: (file: File) => Promise<string>;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<boolean>;
  onDeactivate: (id: string) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const [itemSearch, setItemSearch] = useState("");
  useEffect(() => {
    if (!itemPickerOpen) return;
    const popup = document.getElementById("combo-item-popup");
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); setItemPickerOpen(false); }
      if (event.key !== "Tab") return;
      const controls = Array.from(popup?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)') ?? []);
      const first = controls[0]; const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", handleKey);
    return () => { document.removeEventListener("keydown", handleKey); document.getElementById("combo-add-item-button")?.focus(); };
  }, [itemPickerOpen]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const filteredCombos = combos.filter((combo) => `${combo.name} ${combo.description} ${combo.items.map(({ item }) => item.name).join(" ")}`.toLowerCase().includes(search.trim().toLowerCase()));
  const selectedItems = menuItems.filter((item) => (selections[item.id] ?? 0) > 0);
  const activeItems = menuItems.filter((item) => item.isAvailable && `${item.name} ${item.categoryName}`.toLowerCase().includes(itemSearch.trim().toLowerCase()));
  function closeDialog() {
    if (saving) return;
    setItemPickerOpen(false); setItemSearch("");
    setDialogOpen(false); setForm(emptyComboForm); setSelections({}); setError("");
  }
  async function saveCombo(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    if (!Object.values(selections).some((quantity) => quantity > 0)) { setError("Select at least one menu item for the combo."); return; }
    setSaving(true); setError("");
    try {
      if (await onSubmit(event)) setDialogOpen(false);
      else setError("Unable to save combo. Review the fields and try again.");
    } catch (failure) { setError(failure instanceof Error ? failure.message : "Unable to save combo."); }
    finally { setSaving(false); }
  }
  return (
    <>
      <CatalogSection title="Combos">
        <div className="section-heading compact-heading catalog-panel-heading">
          <div><p>Manage combo meals, pricing, and availability.</p></div>
          <button className="primary-button small" type="button" onClick={() => { setForm(emptyComboForm); setSelections({}); setError(""); setDialogOpen(true); }}><Plus size={16} /><span>Combo Builder</span></button>
        </div>
        <label className="search-box admin-catalog-search"><Search size={17} aria-hidden="true" /><input type="search" aria-label="Search combos" placeholder="Search combos..." value={search} onChange={(event) => setSearch(event.target.value)} /></label>
        <div className="admin-list">
          {filteredCombos.map((combo) => (
            <div className="admin-row combo-row" key={combo.id}>
              <img src={combo.imageUrl || fallbackImages.combo} alt="" />
              <div>
                <strong>{combo.name}</strong>
                <span>
                  {money(combo.priceCents, currency)} - saves {money(combo.savingsCents, currency)}
                </span>
                <span className={`catalog-status ${combo.isAvailable ? "active" : "inactive"}`}>{combo.isAvailable ? "Active" : "Inactive"}</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setForm({
                    id: combo.id,
                    name: combo.name,
                    description: combo.description,
                    price: String(combo.price),
                    imageUrl: combo.imageUrl,
                    rating: String(combo.rating),
                    ratingCount: String(combo.ratingCount),
                    isBestSeller: combo.isBestSeller,
                    isAvailable: combo.isAvailable,
                    sortOrder: combo.sortOrder
                  });
                  setSelections(Object.fromEntries(combo.items.map(({ item, quantity }) => [item.id, quantity])));
                  setError("");
                  setDialogOpen(true);
                }}
              >
                Edit
              </button>
              <button type="button" onClick={() => onDeactivate(combo.id)} title="Deactivate combo">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          {!filteredCombos.length ? <div className="empty-state">{search.trim() ? "No combos match your search." : "No combos yet."}</div> : null}
        </div>
      </CatalogSection>
      {dialogOpen ? <div className="modal-layer">
        <button className="modal-backdrop" type="button" onClick={closeDialog} disabled={saving || itemPickerOpen} aria-label="Close combo dialog" />
        <section className="admin-dialog admin-dialog-wide combo-editor" role="dialog" aria-modal={!itemPickerOpen} aria-hidden={itemPickerOpen || undefined} aria-labelledby="combo-dialog-title">
          <button className="drawer-close" type="button" onClick={closeDialog} disabled={saving || itemPickerOpen} aria-label="Close combo dialog"><X size={20} /></button>
          <div className="admin-dialog-heading"><span><Utensils size={20} /></span><div><small>Catalog</small><h2 id="combo-dialog-title">{form.id ? "Edit combo" : "Combo Builder"}</h2></div></div>
          {error ? <div className="status-banner error" role="alert">{error}</div> : null}
          <fieldset className="combo-dialog-fields" disabled={saving || itemPickerOpen}>
            <form className="admin-form two-col dialog-form" onSubmit={saveCombo}>
              <input required autoFocus aria-label="Combo name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Combo name" />
              <input
                required
                type="number"
                min="0"
                step="0.01"
                aria-label="Combo price"
                value={form.price}
                onChange={(event) => setForm({ ...form, price: event.target.value })}
                placeholder="Combo price"
              />
              <ImageUploadField
                value={form.imageUrl}
                onChange={(imageUrl) => setForm((current) => ({ ...current, imageUrl }))}
                onUpload={onImageUpload}
              />
              <input value={form.rating} onChange={(event) => setForm({ ...form, rating: event.target.value })} placeholder="Rating" />
              <textarea
                className="combo-description-textarea"
                rows={7}
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                placeholder="Description"
              />
              <div className="combo-selected-items">
                <div className="combo-items-heading"><strong>Combo items</strong><button className="secondary-button small" type="button" id="combo-add-item-button" onClick={() => { setItemSearch(""); setItemPickerOpen(true); }}><Plus size={16} /><span>Add Item</span></button></div>
                {selectedItems.length ? selectedItems.map((item) => (
                  <div className="combo-item-card" key={item.id}>
                    <img src={item.imageUrl || fallbackImages.menu} alt={item.name} />
                    <div><strong>{item.name}</strong><span>{money(item.priceCents, currency)}{!item.isAvailable ? " ? Inactive" : ""}</span></div>
                    <label><span>Quantity</span><input type="number" min={1} step={1} aria-label={`Quantity for ${item.name}`} value={selections[item.id]} onChange={(event) => setSelections({ ...selections, [item.id]: Math.max(1, Math.floor(Number(event.target.value) || 1)) })} /></label>
                    <button className="combo-item-remove" type="button" aria-label={`Remove ${item.name} from combo`} onClick={() => { const next = { ...selections }; delete next[item.id]; setSelections(next); }}><Trash2 size={17} /></button>
                  </div>
                )) : <div className="empty-state">No items added. Click Add Item to build your combo.</div>}
              </div>
              <label className="check-line">
                <input
                  type="checkbox"
                  checked={form.isBestSeller}
                  onChange={(event) => setForm({ ...form, isBestSeller: event.target.checked })}
                />
                Best seller
              </label>
              <label className="check-line">
                <input
                  type="checkbox"
                  checked={form.isAvailable}
                  onChange={(event) => setForm({ ...form, isAvailable: event.target.checked })}
                />
                Active
              </label>
              <div className="dialog-actions">
              <button className="secondary-button" type="button" onClick={closeDialog} disabled={saving}>Cancel</button>
              <button className="primary-button" type="submit" disabled={saving}>
                <Save size={17} />
                <span>{saving ? "Saving?" : form.id ? "Save combo" : "Add combo"}</span>
              </button>
              </div>
            </form>
          </fieldset>
        </section>
      </div> : null}
      {dialogOpen && itemPickerOpen ? <div className="modal-layer combo-item-popup-layer">
        <button className="modal-backdrop" type="button" onClick={() => setItemPickerOpen(false)} aria-label="Close add item popup" />
        <section className="admin-dialog combo-item-popup" id="combo-item-popup" role="dialog" aria-modal="true" aria-labelledby="combo-item-popup-title">
          <button className="drawer-close" type="button" onClick={() => setItemPickerOpen(false)} aria-label="Close add item popup"><X size={20} /></button>
          <div className="admin-dialog-heading"><span><Utensils size={20} /></span><div><small>Combo Builder</small><h2 id="combo-item-popup-title">Add items to combo</h2></div></div>
          <label className="search-box"><Search size={17} aria-hidden="true" /><input type="search" autoFocus aria-label="Search active combo items" placeholder="Search active items..." value={itemSearch} onChange={(event) => setItemSearch(event.target.value)} /></label>
          <div className="combo-popup-items">{activeItems.map((item) => <div className="combo-item-card" key={item.id}>
            <img src={item.imageUrl || fallbackImages.menu} alt={item.name} />
            <div><strong>{item.name}</strong><span>{money(item.priceCents, currency)}</span>{selections[item.id] > 0 ? <small>{selections[item.id]} in combo</small> : null}</div>
            <button className="secondary-button small" type="button" aria-label={`Add ${item.name} to combo`} onClick={() => setSelections({ ...selections, [item.id]: (selections[item.id] ?? 0) + 1 })}><Plus size={18} /></button>
          </div>)}</div>
          {!activeItems.length ? <div className="empty-state">{itemSearch.trim() ? "No active items match your search." : "No active menu items available."}</div> : null}
          <div className="dialog-actions"><button className="primary-button" type="button" onClick={() => setItemPickerOpen(false)}>Done</button></div>
        </section>
      </div> : null}
    </>
  );
}

function FeedbackList({ feedback }: { feedback: Feedback[] }) {
  return (
    <div className="feedback-list">
      {feedback.length === 0 ? (
        <div className="empty-state">No feedback yet.</div>
      ) : (
        feedback.map((entry) => (
          <article className="feedback-card" key={entry.id}>
            <div>
              <strong>{entry.customerName}</strong>
              <span>{new Date(entry.createdAt).toLocaleString()}</span>
            </div>
            <Rating rating={entry.rating} count={1} />
            <p>{entry.message || "No written comment."}</p>
            <small>
              {entry.mobile} - {entry.email}
            </small>
          </article>
        ))
      )}
    </div>
  );
}

function SidebarDecoration({ settings }: { settings: Record<string, string> }) {
  const artwork = readSidebarArtwork(settings);
  return artwork ? <div className="sidebar-decoration" aria-hidden="true"><img src={artwork.imageUrl} alt="" draggable={false} /></div> : null;
}

function SettingsSectionCard({ section, children, heading, className = "", onSubmit, saving, disabled, message }: {
  section: SettingsSectionId; children: ReactNode; heading?: ReactNode; className?: string; onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  saving: boolean; disabled: boolean; message?: { saved: boolean; text: string };
}) {
  const [expanded, setExpanded] = useState(true);
  const contentId = useId();
  const content = Children.toArray(children);
  const cardHeading = heading ?? content.shift();
  return <form className={"settings-card " + className} aria-label={settingsSectionLabels[section] + " settings"} onSubmit={onSubmit}>
    <div className="settings-section-heading"><SettingsSectionHeading expanded={expanded} contentId={contentId} label={settingsSectionLabels[section]} disabled={saving} onToggle={() => setExpanded(value => !value)}>{cardHeading}</SettingsSectionHeading></div>
    <fieldset id={contentId} className="settings-card-fields" hidden={!expanded} disabled={saving || !expanded}>
      {content}
      <div className="settings-section-actions"><button className="primary-button" type="submit" disabled={disabled} aria-label={"Save " + settingsSectionLabels[section]}><Save size={17} /><span>{saving ? "Saving…" : "Save changes"}</span></button></div>
    </fieldset>
    {message && expanded ? <div className={message.saved ? "status-banner" : "status-banner error"} role={message.saved ? "status" : "alert"}>{message.text}</div> : null}
  </form>;
}

function SettingsForm({
  settings,
  onImageUpload,
  onBrandUpload = onImageUpload,
  onGalleryUpload = onImageUpload,
  onArtworkUpload = onGalleryUpload,
  mapsApiKey = "",
  onSubmit,
  onThemePreview
}: {
  settings: Record<string, string>;
  onImageUpload: (file: File) => Promise<string>;
  onBrandUpload?: (file: File) => Promise<string>;
  onGalleryUpload?: (file: File) => Promise<string>;
  onArtworkUpload?: (file: File) => Promise<string>;
  mapsApiKey?: string;
  onSubmit: (event: FormEvent<HTMLFormElement>, section: SettingsSectionId) => Promise<boolean> | boolean | void;
  onThemePreview: (theme: UiThemeId) => void;
}) {
  const [savingSection, setSavingSection] = useState<SettingsSectionId | null>(null);
  const saveLock = useRef(false);
  const [sectionMessages, setSectionMessages] = useState<Partial<Record<SettingsSectionId, { saved: boolean; text: string }>>>({});
  async function saveSection(event: FormEvent<HTMLFormElement>, section: SettingsSectionId) {
    event.preventDefault();
    if (saveLock.current) return;
    const form = event.currentTarget;
    if (form.querySelector(".is-uploading") || new FormData(form).get("cafe_gallery_uploading") === "true") {
      setSectionMessages(current => ({ ...current, [section]: { saved: false, text: "Please wait for images to finish uploading." } }));
      return;
    }
    saveLock.current = true;
    setSavingSection(section);
    setSectionMessages(current => ({ ...current, [section]: undefined }));
    try {
      const saved = await onSubmit(event, section) !== false;
      setSectionMessages(current => ({ ...current, [section]: { saved, text: saved ? settingsSectionLabels[section] + " saved." : "Changes were not saved. Check the message above and try again." } }));
      if (saved && section === "maps") form.reset();
      if (saved && section === "email") {
        const apiKeyInput = form.elements.namedItem("resend_api_key") as HTMLInputElement;
        const clearKeyInput = form.elements.namedItem("clear_resend_api_key") as HTMLInputElement;
        apiKeyInput.value = "";
        clearKeyInput.checked = false;
      }
    } catch (error) {
      setSectionMessages(current => ({ ...current, [section]: { saved: false, text: error instanceof Error ? error.message : "Unable to save this section." } }));
    } finally { saveLock.current = false; setSavingSection(null); }
  }
  const cardProps = (section: SettingsSectionId) => ({ section, onSubmit: (event: FormEvent<HTMLFormElement>) => void saveSection(event, section), saving: savingSection === section, disabled: savingSection !== null, message: sectionMessages[section] });
  const tableOptions = buildTableOptions(settings.table_count);
  const [selectedTheme, setSelectedTheme] = useState<UiThemeId>(() => normalizeUiTheme(settings.ui_theme));
  const [artworks, setArtworks] = useState(() => readArtworkLibrary(settings));
  const [selectedProp, setSelectedProp] = useState<SidebarPropId>(() => normalizeSidebarProp(settings.sidebar_prop));
  const [cafeName, setCafeName] = useState(settings.cafe_name ?? "Digital Cafe");
  const [topbarImageUrl, setTopbarImageUrl] = useState(settings.topbar_image_url ?? "");
  const [brandImageUrl, setBrandImageUrl] = useState(settings.brand_image_url ?? "");
  const [selectedQrTable, setSelectedQrTable] = useState<string | null>(null);
  const selectedThemeDetails = uiThemes.find((theme) => theme.id === selectedTheme) ?? uiThemes[0];

  useEffect(() => {
    setSelectedTheme(normalizeUiTheme(settings.ui_theme));
  }, [settings.ui_theme]);

  useEffect(() => {
    setCafeName(settings.cafe_name ?? "Digital Cafe");
  }, [settings.cafe_name]);

  useEffect(() => {
    setTopbarImageUrl(settings.topbar_image_url ?? "");
  }, [settings.topbar_image_url]);

  useEffect(() => { setBrandImageUrl(settings.brand_image_url ?? ""); }, [settings.brand_image_url]);
  useEffect(() => { setSelectedProp(normalizeSidebarProp(settings.sidebar_prop)); }, [settings.sidebar_prop]);
  useEffect(() => { setArtworks(readArtworkLibrary(settings)); }, [settings.artwork_library, settings.about_us_artworks]);

  return (
    <div className="settings-form">
      <SettingsSectionCard {...cardProps("details")}>
        <div className="settings-card-heading"><Store size={22} /><div><h3 id="cafe-details-title">Café details</h3><p>Your café identity, prices, and customer rewards.</p></div></div>
        <div className="settings-fields">
          <label>Café name<input name="cafe_name" value={cafeName} onChange={event => setCafeName(event.target.value)} /></label>
          <label>Currency<input name="currency" defaultValue={settings.currency ?? "USD"} /></label>
          <label>Reward points per 1 currency paid<input name="reward_points_per_currency" type="number" min={0} step="0.01" defaultValue={settings.reward_points_per_currency ?? "0.10"} /></label>
          <label className="settings-field-wide">Service note<textarea name="service_note" defaultValue={settings.service_note ?? ""} /></label>
        </div>
      </SettingsSectionCard>
      <SettingsSectionCard {...cardProps("branding")}>
        <div className="settings-card-heading"><ImagePlus size={22} /><div><h3 id="branding-title">Branding</h3><p>Add your logo and a welcoming menu banner.</p></div></div>
        <div className="settings-fields">
          <div className="brand-image-field"><ImageUploadField inputName="brand_image_url" label="Brand logo" uploadLabel="Upload logo" showPreview={false} value={brandImageUrl} onChange={setBrandImageUrl} onUpload={onBrandUpload} />
          <div className="brand-logo-preview"><BrandLogo imageUrl={brandImageUrl} name={cafeName || "Digital Cafe"} /><small>{brandImageUrl ? "Your uploaded logo" : "Default café logo"}</small></div>
          {brandImageUrl ? <button className="secondary-button" type="button" onClick={() => setBrandImageUrl("")}><Trash2 size={16} />Remove logo</button> : null}</div>
          <div><ImageUploadField className="settings-banner-field" inputName="topbar_image_url" label="Top bar background image" uploadLabel="Upload banner" previewAlt="Top bar background preview" value={topbarImageUrl} onChange={setTopbarImageUrl} onUpload={onImageUpload} />{topbarImageUrl ? <button className="secondary-button" type="button" onClick={() => setTopbarImageUrl("")}>Remove banner</button> : null}</div>
        </div>
      </SettingsSectionCard>
      <SettingsSectionCard {...cardProps("gallery")} heading={<CafeGallerySettingsHeading />}><CafeGallerySettings settings={settings} mapsApiKey={mapsApiKey} onUpload={onGalleryUpload} embedded showHeading={false} /></SettingsSectionCard>
      <SettingsSectionCard {...cardProps("appearance")} className="theme-settings">
        <div className="section-heading compact-heading">
          <div><h3><Palette size={20} />Appearance</h3><p>Preview a theme, then save it for the whole app.</p></div>
        </div>
        <label>App theme<select name="ui_theme" value={selectedTheme} onChange={event => { const theme = normalizeUiTheme(event.target.value); setSelectedTheme(theme); onThemePreview(theme); }}>{uiThemes.map(theme => <option key={theme.id} value={theme.id}>{theme.label}</option>)}</select></label>
        <div className="theme-preview" aria-live="polite"><div className="theme-swatches" aria-hidden="true">{selectedThemeDetails.colors.map(color => <span key={color} style={{ backgroundColor: color }} />)}</div><div><strong>{selectedThemeDetails.label}</strong><span>{selectedThemeDetails.description}</span><small>Typography: {selectedThemeDetails.font}</small></div></div>
        <ArtworkLibrarySettings artworks={artworks} selection={selectedProp} onChange={setArtworks} onSelect={setSelectedProp} onUpload={onArtworkUpload} />
        <div className="sidebar-prop-preview" aria-label="Sidebar artwork preview">
          <SidebarDecoration settings={{ ...settings, artwork_library: JSON.stringify(artworks), sidebar_prop: selectedProp }} />
          <div className="sidebar-prop-preview-content"><Leaf size={22} /><strong>{cafeName || "Digital Cafe"}</strong><span>Menu</span><span>Combos</span><span>My Cart</span></div>
        </div>
      </SettingsSectionCard>
      <div className="settings-integrations">
      <SettingsSectionCard {...cardProps("maps")}>
        <div className="settings-card-heading"><MapPin size={22} /><div><h3 id="maps-settings-title">Google Maps</h3><p>Address search and delivery coverage.</p></div></div>
      <label>Google Maps API key<input name="google_maps_api_key" type="password" autoComplete="new-password" placeholder={settings.google_maps_api_key_configured === "true" ? "Saved key masked. Enter to replace." : "Google Maps browser API key"} /></label>
      {settings.google_maps_api_key_configured === "true" && settings.google_maps_api_key_encrypted !== "true" ? <div className="status-banner error">Configure the server SETTINGS_ENCRYPTION_KEY secret to encrypt the existing Maps key.</div> : null}
      <small>New keys are encrypted and masked here. Leave blank to keep the saved key. Enable Maps JavaScript API and Geocoding API.</small>
      <label className="check-line"><input type="checkbox" name="clear_google_maps_api_key" />Remove saved Maps key</label>
      </SettingsSectionCard>
      <SettingsSectionCard {...cardProps("google")}>
        <div className="settings-card-heading"><KeyRound size={22} /><div><h3 id="google-settings-title">Google Sign-in</h3><p>Let customers sign up and sign in with Google.</p></div></div>
      <label>Google sign-in client ID<input name="google_client_id" defaultValue={settings.google_client_id ?? ""} placeholder="Web OAuth client ID ending in apps.googleusercontent.com" /></label>
      <small>Use a Web application client ID and authorize this app's origin in Google Cloud Console.</small>
      </SettingsSectionCard>
      </div>
      <SettingsSectionCard {...cardProps("email")}>
        <div className="settings-card-heading"><Mail size={22} /><div><h3>Email</h3><p>Send customer verification and password reset emails with Resend.</p></div></div>
        <label>Resend API key<input name="resend_api_key" type="password" autoComplete="new-password" placeholder={settings.resend_api_key_configured === "true" ? "Saved key masked. Enter to replace." : "re_..."} /></label>
        <small>The API key is encrypted and never sent to customers. Leave blank to keep the saved key.</small>
        <label>Sending email address<input name="resend_from_email" type="email" defaultValue={settings.resend_from_email ?? ""} placeholder="hello@your-cafe.com" /></label>
        <small>Use an address from a domain verified in Resend. Both fields are required to send emails.</small>
        <label className="check-line"><input type="checkbox" name="clear_resend_api_key" />Remove saved Resend API key</label>
      </SettingsSectionCard>
      <SettingsSectionCard {...cardProps("tables")} className="table-setup">
        <div className="section-heading compact-heading">
          <div>
            <h3>
              <QrCode size={20} />
              Table QR Codes
            </h3>
            <p>Print or share a code for each table. Scanning opens the menu with that table selected in My Cart.</p>
          </div>
        </div>
        <label>Number of tables<input name="table_count" type="number" min={0} max={100} defaultValue={settings.table_count ?? "12"} /></label>
        <div className="qr-grid">
          {tableOptions.map((table) => {
            const link = tableLink(table);
            return (
              <button
                className="qr-card"
                key={table}
                type="button"
                onClick={() => setSelectedQrTable(table)}
                aria-label={`Open and print the QR code for table ${table}`}
              >
                <img src={qrImageUrl(link)} alt={`QR code for table ${table}`} loading="lazy" />
                <strong>Table {table}</strong>
              </button>
            );
          })}
        </div>
      </SettingsSectionCard>
      {selectedQrTable ? (
        <TableQrDialog
          table={selectedQrTable}
          cafeName={cafeName.trim() || "Digital Cafe"}
          onClose={() => setSelectedQrTable(null)}
        />
      ) : null}
    </div>
  );
}

function TableQrDialog({ table, cafeName, onClose }: { table: string; cafeName: string; onClose: () => void }) {
  const link = tableLink(table);

  return (
    <div className="modal-layer qr-dialog-layer">
      <button className="modal-backdrop" type="button" onClick={onClose} aria-label="Close table QR code" />
      <section className="admin-dialog qr-print-dialog" role="dialog" aria-modal="true" aria-labelledby="table-qr-dialog-title">
        <button className="drawer-close" type="button" onClick={onClose} aria-label="Close table QR code">
          <X size={20} />
        </button>
        <div className="qr-print-sheet">
          <p className="qr-print-cafe">{cafeName}</p>
          <h2 id="table-qr-dialog-title">Table No. {table}</h2>
          <img src={qrImageUrl(link, 420)} alt={`QR code for table ${table}`} />
          <p className="qr-print-instruction">Scan to view the menu and order for this table.</p>
        </div>
        <div className="dialog-actions qr-dialog-actions">
          <button className="primary-button" type="button" onClick={() => window.print()}>
            <Printer size={18} />
            <span>Print QR code</span>
          </button>
        </div>
      </section>
    </div>
  );
}

function buildTableOptions(rawCount: string | undefined) {
  const count = Math.max(0, Math.min(100, Math.floor(Number(rawCount ?? 12) || 0)));
  return Array.from({ length: count }, (_, index) => String(index + 1));
}

export function scannedTableNumberFromLocation() {
  const rawTableNumber = new URLSearchParams(window.location.search).get("table")?.trim() ?? "";
  if (!/^\d{1,3}$/.test(rawTableNumber)) return "";
  const tableNumber = Number(rawTableNumber);
  return tableNumber >= 1 && tableNumber <= 100 ? String(tableNumber) : "";
}

export function tableLink(tableNumber: string) {
  const url = new URL("/", window.location.origin);
  url.searchParams.set("table", tableNumber);
  return url.toString();
}

function qrImageUrl(value: string, size = 180) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=10&data=${encodeURIComponent(value)}`;
}

function toDateTimeLocalValue(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function isAdminRoute() {
  return window.location.pathname.replace(/\/+$/, "") === "/admin";
}

export default App;
